import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { openPrintWindow } from "@/utils/printHelper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Boxes, Plus, Pencil, Trash2, Search, Download, Filter, X, CheckCircle, Clock, RotateCcw, Copy, RefreshCw, ChevronDown, Check, Printer, Scissors, Eye, History, Archive, FileDown, Lock, CalendarIcon, AlertTriangle, Layers, Wrench } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PasswordInput } from "@/components/ui/password-input";
import { QRCodeSVG } from "qrcode.react";
import MachineInventoryView from "./MachineInventoryView";
import ConsumptionInventory from "./ConsumptionInventory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StoreInventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inventoryMode, setInventoryMode] = useState("raw_materials"); // "raw_materials" or "machine_inventory"
  const [inventory, setInventory] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkAddDialogOpen, setIsBulkAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusComment, setStatusComment] = useState("");
  const [newStatus, setNewStatus] = useState("");
  
  // Label size selection state - Simplified to 3x5 with edit option and custom
  const [labelSizeDialogOpen, setLabelSizeDialogOpen] = useState(false);
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [editingStandardSize, setEditingStandardSize] = useState(false);
  const [standardLabelWidth, setStandardLabelWidth] = useState("3");
  const [standardLabelHeight, setStandardLabelHeight] = useState("4.5");
  const [customLabelWidth, setCustomLabelWidth] = useState("");
  const [customLabelHeight, setCustomLabelHeight] = useState("");
  
  // Company logo URL
  const LOGO_URL = "/challenge-pack-logo.png";
  
  // Bit Reel dialog state
  const [bitReelDialogOpen, setBitReelDialogOpen] = useState(false);
  const [bitReelData, setBitReelData] = useState({
    usedWeight: "",
    balanceWeight: "",
    workOrderNumber: ""
  });
  
  // Leftover Material Dialog state
  const [leftoverDialogOpen, setLeftoverDialogOpen] = useState(false);
  const [leftoverData, setLeftoverData] = useState({
    workOrderNumber: "",
    materialType: "bit_reel",
    parentReelId: "",
    parentReelWeight: 0, // Store the original parent reel weight
    usedWeight: "", // Weight used/consumed in WO
    leftoverWeight: "", // Calculated: parentReelWeight - usedWeight
    quantity: "",
    rmName: "",
    reelSize: "",
    comment: ""
  });
  
  // Work orders for dropdown
  const [workOrders, setWorkOrders] = useState([]);
  
  // Work order search states
  const [woSearchLeftover, setWoSearchLeftover] = useState("");
  const [woSearchLeftoverOpen, setWoSearchLeftoverOpen] = useState(false);
  const [woSearchBitReel, setWoSearchBitReel] = useState("");
  const [woSearchBitReelOpen, setWoSearchBitReelOpen] = useState(false);
  
  // Parent Reel search for WO Leftover
  const [parentReelSearch, setParentReelSearch] = useState("");
  const [parentReelOpen, setParentReelOpen] = useState(false);
  
  // Parent Reel search for Add Bit Reel form
  const [addBitReelParentSearch, setAddBitReelParentSearch] = useState("");
  const [addBitReelParentOpen, setAddBitReelParentOpen] = useState(false);
  
  // Selection for print
  const [selectedForPrint, setSelectedForPrint] = useState([]);
  const [corrugatedLabelCounts, setCorrugatedLabelCounts] = useState({});
  
  // Export and Archive states
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archivePassword, setArchivePassword] = useState("");
  const [archiveDays, setArchiveDays] = useState(365);
  const [isExporting, setIsExporting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  
  // Restore from Used Stock state
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [restorePassword, setRestorePassword] = useState("");
  const [itemToRestore, setItemToRestore] = useState(null);
  
  // Export Date Range Dialog state
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  
  // DC (Delivery Challan) creation state
  const [createDC, setCreateDC] = useState(false);
  const [dcData, setDcData] = useState({
    vendor_name: "",
    vehicle_no: "",
    driver_name: "",
    remarks: "",
    manual_items: "" // For manual entry of reel details
  });
  const [showDCPreview, setShowDCPreview] = useState(false);
  const [generatedDC, setGeneratedDC] = useState(null);
  
  // Next Reel ID
  const [nextReelId, setNextReelId] = useState("RMCPI001");
  
  // Delete All Inventory Dialog state
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false);
  const [deleteAllPassword, setDeleteAllPassword] = useState("");
  const [deleteAllSelection, setDeleteAllSelection] = useState({
    availableStock: false,
    stockUsed: false,
    sentForPrinting: false,
    sentForCorrugation: false
  });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Unique Mill and Dealer names from inventory
  const [uniqueMillNames, setUniqueMillNames] = useState([]);
  const [uniqueDealerNames, setUniqueDealerNames] = useState([]);
  
  // Vendors (Dealers) from database
  const [vendors, setVendors] = useState([]);
  
  // Mills from database
  const [mills, setMills] = useState([]);
  
  // Searchable dropdown states
  const [rmNameOpen, setRmNameOpen] = useState(false);
  const [rmNameSearch, setRmNameSearch] = useState("");
  const [millNameOpen, setMillNameOpen] = useState(false);
  const [millNameSearch, setMillNameSearch] = useState("");
  const [dealerNameOpen, setDealerNameOpen] = useState(false);
  const [dealerNameSearch, setDealerNameSearch] = useState("");
  // Non-paper RM Name dropdown
  const [nonPaperRmNameOpen, setNonPaperRmNameOpen] = useState(false);
  const [nonPaperRmNameSearch, setNonPaperRmNameSearch] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    reel_id: "",
    in_date: "",
    reel_size: "",
    page_no: "",
    product: "",
    rm_name: "",
    paper_price: "",
    mill_reel_no: "",
    mill_name: "",
    dealer_name: "",
    qty: "1",
    reel_weight: "",
    status: "available",
    // Shade, GSM, BF for auto-generating RM Name
    shade: "",
    gsm: "",
    bf: "",
    // Non-paper item fields
    item_name: "",
    unit: "Nos",
    description: "",
    tax_preference: "taxable",
    cgst_rate: "9",
    sgst_rate: "9",
    // Bit Reel fields
    is_bit_reel: false,
    parent_reel_id: "",
    original_weight: "",
  });

  // Unit options
  const unitOptions = ["Nos", "Kgs", "Pieces", "Packet", "Meter", "Set", "Box", "Grams", "Litre", "Roll", "Pair", "Dozen"];

  // Paper types (for conditional rendering)
  const PAPER_TYPES = ["Kraft Paper", "Kraft Paper (clr)", "Duplex Paper", "Imported Paper"];
  const isPaperProduct = (productType) => PAPER_TYPES.some(pt => pt.toLowerCase() === productType?.toLowerCase());

  // Product types from database
  const [allProductTypes, setAllProductTypes] = useState([]);

  // Bulk add state
  const [bulkEntries, setBulkEntries] = useState([
    { reel_id: "", in_date: "", reel_size: "", product: "", rm_name: "", reel_weight: "", qty: "1", mill_name: "", dealer_name: "", paper_price: "", mill_reel_no: "", shade: "", gsm: "", bf: "" }
  ]);
  
  // Shade options for RM Name auto-generation
  const SHADE_OPTIONS = ["NS", "D", "GYT", "IMP"];
  
  // Bulk entry searchable dropdown states (track open state per row)
  const [bulkRmNameOpenIdx, setBulkRmNameOpenIdx] = useState(null);
  const [bulkRmNameSearch, setBulkRmNameSearch] = useState("");

  // Unique values for filters
  const [productTypes, setProductTypes] = useState([]);

  useEffect(() => {
    fetchInventory();
    fetchNextReelId();
    fetchMaterials();
    fetchProductTypes();
    fetchVendors();
    fetchMills();
    fetchWorkOrders();
  }, []);

  // Read status filter from URL params
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      setFilterStatus(statusParam);
    }
  }, [searchParams]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/inventory`);
      setInventory(response.data);
      
      // Extract unique product types from inventory for filters
      const products = [...new Set(response.data.map(i => i.product).filter(t => t && t.trim()))];
      setProductTypes(products);
    } catch (error) {
      toast.error("Failed to fetch inventory");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/product-types`);
      // Combine paper types with custom types
      const customTypes = response.data.map(pt => pt.name);
      const allTypes = [...new Set([...PAPER_TYPES, ...customTypes])];
      setAllProductTypes(allTypes);
    } catch (error) {
      console.error("Error fetching product types:", error);
      setAllProductTypes(PAPER_TYPES);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error("Error fetching materials:", error);
    }
  };

  const fetchNextReelId = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/inventory/next-reel-id`);
      setNextReelId(response.data.next_reel_id);
    } catch (error) {
      console.error("Error fetching next reel ID:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendors`);
      setVendors(response.data);
      // Extract unique company names for Dealer dropdown
      const dealers = [...new Set(response.data.map(v => v.company_name).filter(n => n && n.trim()))];
      setUniqueDealerNames(dealers);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchMills = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mills`);
      setMills(response.data);
      // Extract mill names for Mill dropdown
      const millNames = [...new Set(response.data.map(m => m.name).filter(n => n && n.trim()))];
      setUniqueMillNames(millNames);
    } catch (error) {
      console.error("Error fetching mills:", error);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/work-orders`);
      setWorkOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching work orders:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInventory();
    await fetchNextReelId();
    await fetchMaterials();
    await fetchProductTypes();
    await fetchVendors();
    await fetchMills();
    await fetchWorkOrders();
    setRefreshing(false);
    toast.success("Inventory refreshed");
  };

  // Handle leftover material creation
  const handleLeftoverSubmit = async () => {
    if (!leftoverData.workOrderNumber) {
      toast.error("Please select a Work Order");
      return;
    }

    if (leftoverData.materialType === "bit_reel") {
      if (!leftoverData.parentReelId) {
        toast.error("Please select a parent reel");
        return;
      }
      if (!leftoverData.leftoverWeight || parseFloat(leftoverData.leftoverWeight) <= 0) {
        toast.error("Please enter a valid leftover weight for Bit Reel");
        return;
      }
      const leftover = parseFloat(leftoverData.leftoverWeight);
      if (leftover > leftoverData.parentReelWeight) {
        toast.error(`Leftover weight (${leftover} Kg) cannot exceed parent reel weight (${leftoverData.parentReelWeight} Kg)`);
        return;
      }
      const used = leftoverData.parentReelWeight - leftover;
      if (used < 0) {
        toast.error("Invalid weights — used would be negative");
        return;
      }
    }

    if (leftoverData.materialType === "corrugated_roll" && (!leftoverData.quantity || parseInt(leftoverData.quantity) <= 0)) {
      toast.error("Please enter a valid quantity for Corrugated Roll");
      return;
    }

    try {
      // Derive used weight from leftover entry: used = parent - leftover
      const leftoverWeightNum = leftoverData.materialType === "bit_reel"
        ? parseFloat(leftoverData.leftoverWeight)
        : null;
      const derivedUsedWeight = leftoverData.materialType === "bit_reel"
        ? +(leftoverData.parentReelWeight - leftoverWeightNum).toFixed(2)
        : null;

      const payload = {
        work_order_number: leftoverData.workOrderNumber,
        material_type: leftoverData.materialType,
        parent_reel_id: leftoverData.parentReelId || null,
        used_weight: derivedUsedWeight,
        weight: leftoverWeightNum,
        quantity: leftoverData.materialType === "corrugated_roll" ? parseInt(leftoverData.quantity) : null,
        rm_name: leftoverData.rmName?.trim() || null,
        reel_size: leftoverData.reelSize ? parseFloat(leftoverData.reelSize) : null,
        comment: leftoverData.comment || ""
      };

      const response = await axios.post(`${BACKEND_URL}/api/inventory/leftover-material`, payload);
      
      // Show detailed success message
      let successMsg = `${leftoverData.materialType === "bit_reel" ? "Bit Reel" : "Corrugated Roll"} created: ${response.data.item_id}`;
      if (response.data.parent_reel_id && leftoverData.materialType === "bit_reel") {
        successMsg += ` (${leftoverWeightNum} Kg leftover) | Parent (${response.data.parent_reel_id}): ${derivedUsedWeight} Kg used`;
      }
      toast.success(successMsg);
      
      setLeftoverDialogOpen(false);
      setLeftoverData({
        workOrderNumber: "",
        materialType: "bit_reel",
        parentReelId: "",
        parentReelWeight: 0,
        usedWeight: "",
        leftoverWeight: "",
        quantity: "",
        rmName: "",
        reelSize: "",
        comment: ""
      });
      setParentReelSearch("");
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create leftover material");
    }
  };

  const resetForm = () => {
    setFormData({
      reel_id: nextReelId,
      in_date: new Date().toISOString().split('T')[0],
      reel_size: "",
      page_no: "",
      product: "",
      rm_name: "",
      paper_price: "",
      mill_reel_no: "",
      mill_name: "",
      dealer_name: "",
      qty: "1",
      reel_weight: "",
      status: "available",
      // Non-paper item fields
      item_name: "",
      unit: "Nos",
      description: "",
      tax_preference: "taxable",
      cgst_rate: "9",
      sgst_rate: "9",
      // Bit Reel fields
      is_bit_reel: false,
      parent_reel_id: "",
      original_weight: "",
    });
    setRmNameSearch("");
    setMillNameSearch("");
    setDealerNameSearch("");
  };

  // Handle RM Name selection - auto-fill product type from materials
  const handleRMNameSelect = (rmName) => {
    const material = materials.find(m => m.product_code === rmName);
    if (material) {
      setFormData(prev => ({
        ...prev,
        rm_name: rmName,
        product: material.product_type || prev.product,
        reel_size: material.reel_size_inches?.toString() || prev.reel_size,
      }));
    } else {
      setFormData(prev => ({ ...prev, rm_name: rmName }));
    }
    setRmNameOpen(false);
  };

  const handleAddItem = async () => {
    if (!formData.reel_id.trim()) {
      toast.error("Stock ID is required");
      return;
    }

    try {
      const payload = {
        ...formData,
        reel_size: formData.reel_size ? parseFloat(formData.reel_size) : null,
        page_no: formData.page_no ? parseFloat(formData.page_no) : null,
        paper_price: formData.paper_price ? parseFloat(formData.paper_price) : null,
        qty: formData.qty ? parseFloat(formData.qty) : 1,
        reel_weight: formData.reel_weight ? parseFloat(formData.reel_weight) : null,
        // Bit Reel fields
        is_bit_reel: formData.is_bit_reel || false,
        parent_reel_id: formData.is_bit_reel ? formData.parent_reel_id : null,
        original_weight: formData.is_bit_reel && formData.original_weight ? parseFloat(formData.original_weight) : null,
      };

      await axios.post(`${BACKEND_URL}/api/inventory`, payload);
      toast.success(formData.is_bit_reel ? "Bit Reel added successfully" : "Stock added successfully");
      setIsAddDialogOpen(false);
      fetchInventory();
      fetchNextReelId();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add stock");
    }
  };

  const handleEditItem = async () => {
    if (!formData.reel_id.trim()) {
      toast.error("Stock ID is required");
      return;
    }

    try {
      const payload = {
        ...formData,
        reel_size: formData.reel_size ? parseFloat(formData.reel_size) : null,
        page_no: formData.page_no ? parseFloat(formData.page_no) : null,
        paper_price: formData.paper_price ? parseFloat(formData.paper_price) : null,
        qty: formData.qty ? parseFloat(formData.qty) : 1,
        reel_weight: formData.reel_weight ? parseFloat(formData.reel_weight) : null,
      };

      await axios.put(`${BACKEND_URL}/api/inventory/${selectedItem.id}`, payload);
      toast.success("Stock updated successfully");
      setIsEditDialogOpen(false);
      setSelectedItem(null);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update stock");
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/inventory/${selectedItem.id}`);
      
      // Check if a bit reel was promoted
      if (response.data.bit_reel_promoted) {
        const promoted = response.data.promoted_reel;
        toast.success(
          `Stock deleted! Bit reel ${promoted.old_reel_id} promoted to ${promoted.new_reel_id} with original weight ${promoted.restored_weight} Kg`,
          { duration: 5000 }
        );
      } else {
        toast.success("Stock deleted successfully");
      }
      
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchInventory();
    } catch (error) {
      toast.error("Failed to delete stock");
    }
  };

  // Handle Delete All Inventory
  const handleDeleteAll = async () => {
    // Validate password
    if (deleteAllPassword !== "Cpidelete#909") {
      toast.error("Invalid password");
      return;
    }
    
    // Validate selection
    if (!deleteAllSelection.availableStock && !deleteAllSelection.stockUsed && !deleteAllSelection.sentForPrinting && !deleteAllSelection.sentForCorrugation) {
      toast.error("Please select at least one stock type to delete");
      return;
    }
    
    setIsDeleting(true);
    try {
      const statusesToDelete = [];
      if (deleteAllSelection.availableStock) statusesToDelete.push("available");
      if (deleteAllSelection.stockUsed) statusesToDelete.push("stock_used");
      if (deleteAllSelection.sentForPrinting) { statusesToDelete.push("printed"); statusesToDelete.push("sent_for_printing"); }
      if (deleteAllSelection.sentForCorrugation) { statusesToDelete.push("sent_for_corrugation"); statusesToDelete.push("received_corrugation"); }
      
      const response = await axios.post(`${BACKEND_URL}/api/inventory/delete-all`, {
        statuses: statusesToDelete
      });
      
      const deletedCount = response.data.deleted_count || 0;
      toast.success(`Successfully deleted ${deletedCount} inventory items`);
      
      setIsDeleteAllDialogOpen(false);
      setDeleteAllPassword("");
      setDeleteAllSelection({ availableStock: false, stockUsed: false, sentForPrinting: false, sentForCorrugation: false });
      fetchInventory();
    } catch (error) {
      console.error("Delete all error:", error);
      toast.error("Failed to delete inventory");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle status change from table dropdown
  const handleStatusChange = async (item, status) => {
    if (status === "stock_used" || status === "return") {
      setSelectedItem(item);
      setNewStatus(status);
      setStatusComment("");
      setStatusChangeDialogOpen(true);
    } else if (status === "bit_reel") {
      // Open bit reel dialog for partial usage
      setSelectedItem(item);
      setBitReelData({
        usedWeight: "",
        balanceWeight: "",
        workOrderNumber: ""
      });
      setBitReelDialogOpen(true);
    } else {
      try {
        await axios.put(`${BACKEND_URL}/api/inventory/${item.id}`, {
          status: status,
          used_comment: ""
        });
        toast.success("Status updated");
        fetchInventory();
      } catch (error) {
        toast.error("Failed to update status");
      }
    }
  };

  // Handle Bit Reel creation
  const handleBitReelConfirm = async () => {
    const usedWeight = parseFloat(bitReelData.usedWeight);
    const balanceWeight = parseFloat(bitReelData.balanceWeight);
    
    if (isNaN(usedWeight) || usedWeight <= 0) {
      toast.error("Please enter a valid used weight");
      return;
    }
    
    if (isNaN(balanceWeight) || balanceWeight <= 0) {
      toast.error("Please enter a valid balance weight");
      return;
    }
    
    const originalWeight = selectedItem?.reel_weight || 0;
    if (usedWeight + balanceWeight > originalWeight * 1.1) {
      toast.error(`Total (${usedWeight + balanceWeight} Kg) exceeds original weight (${originalWeight} Kg)`);
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/inventory/bit-reel`, {
        original_item_id: selectedItem.id,
        used_weight: usedWeight,
        balance_weight: balanceWeight,
        used_comment: statusComment,
        work_order_number: bitReelData.workOrderNumber || null
      });
      
      toast.success(`Bit reel created: ${response.data.bit_reel_id} (${balanceWeight} Kg)`);
      setBitReelDialogOpen(false);
      setSelectedItem(null);
      setStatusComment("");
      setBitReelData({ usedWeight: "", balanceWeight: "", workOrderNumber: "" });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create bit reel");
    }
  };

  const handleStatusChangeConfirm = async () => {
    if (!statusComment.trim()) {
      toast.error("Comment is required");
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/inventory/${selectedItem.id}`, {
        status: newStatus,
        used_comment: statusComment
      });
      toast.success("Status updated");
      setStatusChangeDialogOpen(false);
      setSelectedItem(null);
      setStatusComment("");
      fetchInventory();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const openViewDialog = (item) => {
    setViewItem(item);
    setIsViewDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setFormData({
      reel_id: item.reel_id || "",
      in_date: item.in_date || "",
      reel_size: item.reel_size?.toString() || "",
      page_no: item.page_no?.toString() || "",
      product: item.product || "",
      rm_name: item.rm_name || "",
      paper_price: item.paper_price?.toString() || "",
      mill_reel_no: item.mill_reel_no?.toString() || "",
      mill_name: item.mill_name || "",
      dealer_name: item.dealer_name || "",
      qty: item.qty?.toString() || "1",
      reel_weight: item.reel_weight?.toString() || "",
      status: item.status || "available",
      // Non-paper item fields
      item_name: item.item_name || "",
      unit: item.unit || "Nos",
      description: item.description || "",
      tax_preference: item.tax_preference || "taxable",
      cgst_rate: item.cgst_rate?.toString() || "9",
      sgst_rate: item.sgst_rate?.toString() || "9",
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  // Archive function (password protected)
  const handleArchive = async () => {
    if (!archivePassword) {
      toast.error("Please enter password");
      return;
    }
    
    try {
      setIsArchiving(true);
      const response = await axios.post(`${BACKEND_URL}/api/archive/inventory`, {
        password: archivePassword,
        days_old: archiveDays,
        collection: "inventory"
      });
      
      toast.success(response.data.message);
      setIsArchiveDialogOpen(false);
      setArchivePassword("");
      fetchInventory(); // Refresh data
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Invalid password");
      } else {
        toast.error("Failed to archive data");
      }
    } finally {
      setIsArchiving(false);
    }
  };

  // Restore from Stock Used (password protected)
  const handleRestoreConfirm = async () => {
    if (!restorePassword) {
      toast.error("Please enter password");
      return;
    }
    
    if (restorePassword !== "Cpiinv#909") {
      toast.error("Invalid password");
      return;
    }
    
    if (!itemToRestore) {
      toast.error("No item selected for restore");
      return;
    }
    
    try {
      await axios.put(`${BACKEND_URL}/api/inventory/${itemToRestore.id}`, {
        status: "available",
        used_comment: null,
        used_in_work_order: null
      });
      toast.success(`${itemToRestore.reel_id} restored to Available Stock`);
      setIsRestoreDialogOpen(false);
      setRestorePassword("");
      setItemToRestore(null);
      fetchInventory();
    } catch (error) {
      toast.error("Failed to restore item");
    }
  };

  // Bulk Add Functions
  const openBulkAddDialog = () => {
    setBulkEntries([
      { reel_id: nextReelId, in_date: new Date().toISOString().split('T')[0], reel_size: "", product: "", rm_name: "", reel_weight: "", qty: "1", mill_name: "", dealer_name: "", paper_price: "", shade: "", gsm: "", bf: "" }
    ]);
    setIsBulkAddDialogOpen(true);
  };

  const addBulkRow = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/inventory/next-reel-id`);
      const currentMaxNum = bulkEntries.reduce((max, entry) => {
        if (entry.reel_id && entry.reel_id.startsWith('RMCPI')) {
          const num = parseInt(entry.reel_id.substring(5)) || 0;
          return Math.max(max, num);
        }
        return max;
      }, parseInt(response.data.next_number) - 1);
      
      const nextNum = currentMaxNum + 1;
      const newReelId = `RMCPI${String(nextNum).padStart(3, '0')}`;
      
      // Get last entry to clone ALL fields except reel_weight
      const lastEntry = bulkEntries[bulkEntries.length - 1] || {};
      
      setBulkEntries(prev => [...prev, {
        reel_id: newReelId,
        in_date: lastEntry.in_date || new Date().toISOString().split('T')[0],
        reel_size: lastEntry.reel_size || "",
        product: lastEntry.product || "",
        rm_name: lastEntry.rm_name || "",
        reel_weight: "", // Only this field is NOT cloned
        qty: lastEntry.qty || "1",
        mill_name: lastEntry.mill_name || "",
        dealer_name: lastEntry.dealer_name || "",
        paper_price: lastEntry.paper_price || "",
        mill_reel_no: lastEntry.mill_reel_no || "",
        shade: lastEntry.shade || "",
        gsm: lastEntry.gsm || "",
        bf: lastEntry.bf || ""
      }]);
    } catch (error) {
      const lastEntry = bulkEntries[bulkEntries.length - 1];
      let nextNum = 1;
      if (lastEntry.reel_id && lastEntry.reel_id.startsWith('RMCPI')) {
        nextNum = parseInt(lastEntry.reel_id.substring(5)) + 1 || 1;
      }
      setBulkEntries(prev => [...prev, {
        reel_id: `RMCPI${String(nextNum).padStart(3, '0')}`,
        in_date: lastEntry.in_date || new Date().toISOString().split('T')[0],
        reel_size: lastEntry.reel_size || "",
        product: lastEntry.product || "",
        rm_name: lastEntry.rm_name || "",
        reel_weight: "", // Only this field is NOT cloned
        qty: lastEntry.qty || "1",
        mill_name: lastEntry.mill_name || "",
        dealer_name: lastEntry.dealer_name || "",
        paper_price: lastEntry.paper_price || "",
        mill_reel_no: lastEntry.mill_reel_no || "",
        shade: lastEntry.shade || "",
        gsm: lastEntry.gsm || "",
        bf: lastEntry.bf || ""
      }]);
    }
  };

  const removeBulkRow = (index) => {
    if (bulkEntries.length > 1) {
      setBulkEntries(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Helper function to generate RM Name from shade, gsm, and bf
  const generateRmName = (shade, gsm, bf) => {
    if (shade && gsm) {
      return bf ? `${shade}${gsm}${bf}` : `${shade}${gsm}`;
    }
    return "";
  };

  // Helper function to get product type from shade (must match actual DB values)
  const getProductTypeFromShade = (shade) => {
    const shadeToProductType = {
      'NS': 'Kraft Paper',
      'D': 'Plain Duplex',
      'GYT': 'Plain Gyt',
      'IMP': 'Imported Paper'
    };
    return shadeToProductType[shade] || "";
  };

  // Helper function to get product type from materials database by matching rm_name (shade+gsm+bf)
  const getProductTypeFromMaterials = (rmName) => {
    if (!rmName || !materials || materials.length === 0) return null;
    // Match by product_code or item_name
    const material = materials.find(m => 
      m.product_code?.toLowerCase() === rmName.toLowerCase() ||
      m.item_name?.toLowerCase() === rmName.toLowerCase()
    );
    return material?.product_type || null;
  };

  const updateBulkEntry = (index, field, value) => {
    setBulkEntries(prev => prev.map((entry, i) => {
      if (i === index) {
        // Handle shade, gsm, bf changes to auto-generate rm_name and product type
        if (field === 'shade' || field === 'gsm' || field === 'bf') {
          const newEntry = { ...entry, [field]: value };
          const newShade = field === 'shade' ? value : entry.shade;
          const newGsm = field === 'gsm' ? value : entry.gsm;
          const newBf = field === 'bf' ? value : entry.bf;
          
          // Auto-fill product type from shade (shade is always the source of truth)
          if (newShade) {
            const productType = getProductTypeFromShade(newShade);
            if (productType) {
              newEntry.product = productType;
            }
          }
          
          // Auto-generate RM Name when shade + gsm are filled (bf optional)
          const generatedRmName = generateRmName(newShade, newGsm, newBf);
          if (generatedRmName) {
            newEntry.rm_name = generatedRmName;
          }
          return newEntry;
        }
        // Handle rm_name selection from dropdown (existing functionality)
        if (field === 'rm_name') {
          const material = materials.find(m => m.product_code === value);
          if (material) {
            return {
              ...entry,
              rm_name: value,
              product: material.product_type || entry.product,
              reel_size: material.reel_size_inches?.toString() || entry.reel_size,
            };
          }
        }
        return { ...entry, [field]: value };
      }
      return entry;
    }));
  };

  // Handler for shade/gsm/bf changes in single Add Stock form
  const handleFormDataShadeGsmBf = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    const shade = field === 'shade' ? value : formData.shade;
    const gsm = field === 'gsm' ? value : formData.gsm;
    const bf = field === 'bf' ? value : formData.bf;
    
    // Auto-fill product type from shade (shade is always the source of truth)
    if (shade) {
      const productType = getProductTypeFromShade(shade);
      if (productType) {
        newFormData.product = productType;
      }
    }
    
    // Auto-generate RM Name when shade + gsm are filled (bf optional)
    const generatedRmName = generateRmName(shade, gsm, bf);
    if (generatedRmName) {
      newFormData.rm_name = generatedRmName;
    }
    
    setFormData(newFormData);
  };

  const handleBulkAdd = async () => {
    const validEntries = bulkEntries.filter(e => e.reel_id && e.reel_id.trim());
    if (validEntries.length === 0) {
      toast.error("Please add at least one valid entry");
      return;
    }

    try {
      const payload = validEntries.map(entry => ({
        reel_id: entry.reel_id,
        in_date: entry.in_date,
        reel_size: entry.reel_size ? parseFloat(entry.reel_size) : null,
        product: entry.product,
        rm_name: entry.rm_name,
        reel_weight: entry.reel_weight ? parseFloat(entry.reel_weight) : null,
        qty: entry.qty ? parseFloat(entry.qty) : 1,
        mill_name: entry.mill_name,
        dealer_name: entry.dealer_name,
        paper_price: entry.paper_price ? parseFloat(entry.paper_price) : null,
        mill_reel_no: entry.mill_reel_no || "",
        status: "available"
      }));

      const response = await axios.post(`${BACKEND_URL}/api/inventory/bulk`, payload);
      toast.success(`Added ${response.data.created} items (${response.data.skipped} skipped)`);
      setIsBulkAddDialogOpen(false);
      fetchInventory();
      fetchNextReelId();
    } catch (error) {
      toast.error("Failed to add bulk entries");
    }
  };

  // Filter and search inventory - base filtering
  const baseFilteredInventory = inventory.filter(item => {
    let matchesSearch = true;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      switch (searchField) {
        case "size":
          matchesSearch = item.reel_size?.toString().includes(term);
          break;
        case "rm_name":
          matchesSearch = item.rm_name?.toLowerCase().includes(term);
          break;
        case "weight":
          matchesSearch = item.reel_weight?.toString().includes(term);
          break;
        case "product":
          matchesSearch = item.product?.toLowerCase().includes(term);
          break;
        case "date":
          matchesSearch = item.in_date === searchTerm;
          break;
        default:
          matchesSearch = 
            item.reel_id?.toLowerCase().includes(term) ||
            item.rm_name?.toLowerCase().includes(term) ||
            item.product?.toLowerCase().includes(term) ||
            item.reel_size?.toString().includes(term) ||
            item.reel_weight?.toString().includes(term) ||
            item.mill_name?.toLowerCase().includes(term) ||
            item.dealer_name?.toLowerCase().includes(term) ||
            item.in_date?.includes(term);
      }
    }
    
    const matchesProduct = filterProduct === "all" || item.product === filterProduct;
    
    return matchesSearch && matchesProduct;
  });

  // Split into Available Stock and Used Stock
  const availableInventory = baseFilteredInventory.filter(item => 
    item.status === "available" || item.status === "return"
  );

  // Calculate days in inventory for aging alerts
  const getDaysInInventory = (inDate) => {
    if (!inDate) return 0;
    const dateIn = new Date(inDate);
    const today = new Date();
    const diffTime = today.getTime() - dateIn.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };
  const AGING_THRESHOLD = 60;

  // When aging filter is active, show only aging items in the table
  const displayAvailableInventory = filterStatus === "aging" 
    ? availableInventory.filter(item => getDaysInInventory(item.in_date) > AGING_THRESHOLD)
    : availableInventory;
  
  const usedInventory = baseFilteredInventory.filter(item => 
    item.status === "stock_used" || item.status === "used"
  );

  const printedInventory = baseFilteredInventory.filter(item => 
    item.status === "printed" || item.status === "sent_for_printing" || item.status === "received"
  );

  const corrugationInventory = baseFilteredInventory.filter(item => 
    item.status === "sent_for_corrugation" || item.status === "received_corrugation"
  );

  // For backward compatibility - use the status filter when explicitly set
  const filteredInventory = filterStatus === "all" 
    ? baseFilteredInventory 
    : filterStatus === "printed"
    ? baseFilteredInventory.filter(item => item.status === "printed" || item.status === "sent_for_printing")
    : filterStatus === "sent_for_corrugation"
    ? baseFilteredInventory.filter(item => item.status === "sent_for_corrugation" || item.status === "received_corrugation")
    : filterStatus === "aging"
    ? baseFilteredInventory.filter(item => item.status === "available" && getDaysInInventory(item.in_date) > AGING_THRESHOLD)
    : baseFilteredInventory.filter(item => item.status === filterStatus);

  // Stats
  const availableCount = inventory.filter(i => i.status === "available").length;
  const usedCount = inventory.filter(i => i.status === "stock_used" || i.status === "used").length;
  const returnCount = inventory.filter(i => i.status === "return").length;
  const printedCount = inventory.filter(i => i.status === "printed" || i.status === "sent_for_printing" || i.status === "received").length;
  const corrugationCount = inventory.filter(i => i.status === "sent_for_corrugation" || i.status === "received_corrugation").length;
  const totalWeight = inventory.filter(i => i.status === "available").reduce((sum, i) => sum + (i.reel_weight || 0), 0);

  const agingItems = availableInventory.filter(item => getDaysInInventory(item.in_date) > AGING_THRESHOLD);
  const agingCount = agingItems.length;

  // Export to CSV with date range filter
  const handleExportCSV = () => {
    // Filter inventory based on date range
    let dataToExport = inventory;
    
    if (exportFromDate || exportToDate) {
      dataToExport = inventory.filter(item => {
        const itemDate = item.in_date || "";
        if (exportFromDate && itemDate < exportFromDate) return false;
        if (exportToDate && itemDate > exportToDate) return false;
        return true;
      });
    }
    
    const headers = ["Stock ID", "In Date", "Reel Size", "Page No", "Product", "RM Name", "Paper Price", "Mill Reel No", "Mill Name", "Dealer Name", "Qty", "Reel Weight", "Status", "Comment"];
    const csvContent = [
      headers.join(","),
      ...dataToExport.map(i => [
        i.reel_id || "",
        i.in_date || "",
        i.reel_size || "",
        i.page_no || "",
        `"${i.product || ""}"`,
        i.rm_name || "",
        i.paper_price || "",
        i.mill_reel_no || "",
        `"${i.mill_name || ""}"`,
        `"${i.dealer_name || ""}"`,
        i.qty || 1,
        i.reel_weight || "",
        i.status || "available",
        `"${i.used_comment || ""}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dateRangeSuffix = exportFromDate || exportToDate ? `_${exportFromDate || "all"}_to_${exportToDate || "all"}` : "";
    a.download = `inventory_${new Date().toISOString().split('T')[0]}${dateRangeSuffix}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${dataToExport.length} inventory items to CSV`);
    setIsExportDialogOpen(false);
    setExportFromDate("");
    setExportToDate("");
  };

  // Print Label Functions
  const toggleSelectForPrint = (itemId) => {
    setSelectedForPrint(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  const selectAllForPrint = () => {
    const allIds = filteredInventory.map(i => i.id);
    if (selectedForPrint.length === filteredInventory.length) {
      setSelectedForPrint([]);
    } else {
      setSelectedForPrint(allIds);
    }
  };

  const openPrintDialog = () => {
    if (selectedForPrint.length === 0) {
      toast.error("Please select at least one item to print");
      return;
    }
    // Pre-populate corrugated roll label counts with their qty
    const counts = {};
    const selectedItems = inventory.filter(i => selectedForPrint.includes(i.id));
    selectedItems.forEach(item => {
      if (item.product === 'Corrugated Roll') {
        counts[item.id] = parseInt(item.qty) || 1;
      }
    });
    setCorrugatedLabelCounts(counts);
    setIsPrintDialogOpen(true);
  };

  // State for Google Drive upload progress
  const [uploadingLabels, setUploadingLabels] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const handlePrintLabels = async () => {
    const itemsToPrint = inventory.filter(i => selectedForPrint.includes(i.id));
    
    // Determine label size - use editable standard size or custom
    let labelWidth, labelHeight;
    if (useCustomSize && customLabelWidth && customLabelHeight) {
      labelWidth = parseFloat(customLabelWidth);
      labelHeight = parseFloat(customLabelHeight);
    } else {
      // Use standard label size (editable)
      labelWidth = parseFloat(standardLabelWidth) || 3;
      labelHeight = parseFloat(standardLabelHeight) || 4.5;
    }

    setUploadingLabels(true);
    setUploadProgress({ current: 0, total: itemsToPrint.length });

    // Upload each label to Google Drive and collect the results
    const uploadedLabels = [];
    
    for (let i = 0; i < itemsToPrint.length; i++) {
      const item = itemsToPrint[i];
      setUploadProgress({ current: i + 1, total: itemsToPrint.length });
      
      try {
        let response;
        
        // Use different endpoint for Corrugated Rolls (2x2 inch labels)
        if (item.product === 'Corrugated Roll') {
          response = await axios.post(`${BACKEND_URL}/api/labels/corrugated-roll/generate-and-upload`, {
            stock_id: item.reel_id,
            rm_name: item.rm_name || '',
            qty: parseInt(item.qty) || 0
          });
        } else {
          // Standard label for other products
          response = await axios.post(`${BACKEND_URL}/api/labels/generate-and-upload`, {
            stock_id: item.reel_id,
            rm_name: item.rm_name || '',
            reel_size: item.reel_size?.toString() || '',
            mill_reel_no: item.mill_reel_no || '',
            weight: item.reel_weight?.toString() || '',
            product: item.product || '',
            mill_name: item.mill_name || '',
            in_date: item.in_date || '',
            label_width: labelWidth,
            label_height: labelHeight,
            is_bit_reel: item.is_bit_reel || false,
            parent_stock_id: item.parent_reel_id || ''
          });
        }
        
        if (response.data.success) {
          // Check if it was already uploaded
          if (response.data.already_uploaded) {
            toast.info(`Label for ${item.reel_id} was already uploaded. Using existing link.`);
          }
          
          const labelData = {
            ...item,
            driveLink: response.data.shareable_link,
            directLink: response.data.direct_link,
            qrUrl: response.data.direct_link || response.data.shareable_link || `https://cpicore.in/label/${item.reel_id}`,
            filename: response.data.filename,
            labelNumber: response.data.label_number,
            labelSize: item.product === 'Corrugated Roll' ? '2x2' : `${labelWidth}x${labelHeight}`
          };
          
          // For Corrugated Rolls, push N copies based on user-specified label count
          if (item.product === 'Corrugated Roll') {
            const copyCount = corrugatedLabelCounts[item.id] || 1;
            for (let c = 0; c < copyCount; c++) {
              uploadedLabels.push({ ...labelData, _copyIndex: c + 1 });
            }
          } else {
            uploadedLabels.push(labelData);
          }
        } else {
          toast.error(`Failed to upload label for ${item.reel_id}: ${response.data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error(`Error uploading label for ${item.reel_id}:`, error);
        toast.error(`Failed to upload label for ${item.reel_id}`);
      }
    }

    setUploadingLabels(false);

    if (uploadedLabels.length === 0) {
      toast.error("No labels were uploaded successfully");
      return;
    }

    // Show success message with links
    toast.success(`${uploadedLabels.length} label(s) uploaded to Google Drive!`);
    
    // Generate labels HTML for print window
    // Use direct QR code image URLs instead of script-based generation
    const labelsHTML = uploadedLabels.map((item, idx) => {
      // Construct the QR code URL using external API
      const qrUrl = item.directLink || item.driveLink || `https://cpicore.in/label/${item.reel_id}`;
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}&ecc=M`;
      
      // Special handling for Corrugated Roll labels (2x2 inch)
      if (item.product === 'Corrugated Roll') {
        const rollNo = item._copyIndex || '';
        return '<div class="label label-small" data-index="' + idx + '" data-type="corrugated"><div class="label-inner-small">' +
          '<div class="cr-stock-id">' + (item.reel_id || '-') + '</div>' +
          '<div class="cr-rm-name">' + (item.rm_name || '-') + '</div>' +
          '<div class="cr-qty">Qty: ' + (item.qty || 0) + ' Nos</div>' +
          (rollNo ? '<div class="cr-roll-no">Roll No ' + rollNo + '</div>' : '') +
          '<div class="cr-qr-section">' +
          '<img class="qr-code-img-small" src="' + qrImageUrl + '" alt="QR Code" style="width:65px;height:65px;" />' +
          '</div>' +
          '<div class="cr-footer">Corrugated Roll</div>' +
          '</div></div>';
      }
      
      // Standard label for other products
      return '<div class="label" data-index="' + idx + '" data-type="standard"><div class="label-inner">' +
        '<!-- Header with Logo -->' +
        '<div class="header">' +
        '<img src="' + LOGO_URL + '" alt="Logo" class="logo" onerror="this.style.display=\'none\';" />' +
        '<div class="header-text"><h2>' + (item.is_bit_reel ? 'BIT REEL LABEL' : 'RM LABEL') + '</h2><p>Challenge Pack Industries</p></div>' +
        '</div>' +
        '<!-- Stock ID Box -->' +
        '<div class="stock-id-box">' +
        '<div class="stock-id-label">Stock ID</div>' +
        '<div class="stock-id-value">' + (item.reel_id || '-') + '</div>' +
        '</div>' +
        '<!-- Data Table -->' +
        '<table class="content-table"><tbody>' +
        '<tr><td class="field-label">Reel Size:</td><td class="field-value">' + (item.reel_size ? item.reel_size + '"' : '-') + '</td></tr>' +
        '<tr class="alt-row"><td class="field-label">Reel No:</td><td class="field-value">' + (item.mill_reel_no || '-') + '</td></tr>' +
        '<tr><td class="field-label">RM Name:</td><td class="field-value">' + (item.rm_name || '-') + '</td></tr>' +
        '<tr class="alt-row"><td class="field-label">Mill Name:</td><td class="field-value">' + (item.mill_name || '-') + '</td></tr>' +
        '<tr><td class="field-label">In Date:</td><td class="field-value">' + (item.in_date || '-') + '</td></tr>' +
        '<tr class="weight-row"><td class="field-label">Weight:</td><td class="field-value weight-value">' + (item.reel_weight ? item.reel_weight + ' kg' : '-') + '</td></tr>' +
        '<tr class="alt-row"><td class="field-label">Product:</td><td class="field-value">' + (item.product || '-') + '</td></tr>' +
        '</tbody></table>' +
        '<!-- QR Section at Bottom -->' +
        '<div class="qr-section">' +
        '<div class="qr-hint">Scan QR Code to view label</div>' +
        '<div class="qr-container"><img class="qr-code-img" src="' + qrImageUrl + '" alt="QR Code" /></div>' +
        '</div>' +
        '<div class="footer">Label: ' + (item.labelNumber || 'N/A') + ' | Printed: ' + new Date().toLocaleDateString() + '</div>' +
        '</div></div>';
    }).join('');

    // Dynamic label size with logo at top, QR at bottom design
    // Using direct QR image URLs instead of JavaScript-based generation
    const htmlContent = '<!DOCTYPE html><html><head>' +
      '<title> </title>' +  // blank title so the browser default print header is minimal
      '<style>' +
      '* { margin: 0; padding: 0; box-sizing: border-box; }' +
      '@page { size: ' + labelWidth + 'in ' + labelHeight + 'in; margin: 0; }' +
      'html, body { font-family: Arial, sans-serif; margin: 0; padding: 0; background: white; }' +
      /* Non-printing instruction banner — visible in the print-preview window only */
      '.print-tip { background: #fff3cd; border: 2px solid #f59e0b; padding: 10px 14px; margin: 10px; font-family: Arial, sans-serif; font-size: 13px; color: #78350f; border-radius: 6px; line-height: 1.4; }' +
      '.print-tip strong { color: #b45309; font-size: 14px; }' +
      '.print-tip ul { margin: 6px 0 0 22px; }' +
      '.labels-container { display: flex; flex-wrap: wrap; }' +
      /* The .label takes the full sticker page (3in x 4.5in) but the visible content */
      /* (.label-inner) is locked to a smaller height so that browser-added headers/footers */
      /* never push content onto the next sticker. */
      '.label { width: ' + labelWidth + 'in; height: ' + labelHeight + 'in; padding: 3px; page-break-after: always; page-break-inside: avoid; box-sizing: border-box; background: white; overflow: hidden; }' +
      '/* Corrugated Roll 2x2 inch label styles */' +
      '.label-small { width: 2in; height: 2in; padding: 4px; }' +
      '.label-inner-small { border: 2px solid #7c3aed; padding: 8px; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: space-between; background: white; border-radius: 4px; }' +
      '.cr-stock-id { font-size: 14px; font-weight: bold; color: #7c3aed; text-align: center; }' +
      '.cr-rm-name { font-size: 11px; font-weight: bold; color: #1a1a1a; text-align: center; word-break: break-word; max-width: 100%; }' +
      '.cr-qty { font-size: 9px; color: #4a4a4a; text-align: center; }' +
      '.cr-roll-no { font-size: 10px; font-weight: bold; color: #7c3aed; text-align: center; }' +
      '.cr-qr-section { flex: 1; display: flex; align-items: center; justify-content: center; min-height: 70px; }' +
      '.qr-code-img-small { width: 65px; height: 65px; }' +
      '.cr-footer { font-size: 7px; color: #6c757d; text-align: center; }' +
      '/* Standard label styles — tuned to fit 3in x 4.5in paper without overflow */' +
      /* Inner content is clamped to 3.3in max so that even when the browser adds */
      /* its default print header/footer (~1in total) the content stays on ONE sticker. */
      '.label-inner { border: 1.5px solid #212529; padding: 4px 6px; height: 3.3in; max-height: 3.3in; display: flex; flex-direction: column; background: white; overflow: hidden; }' +
      '/* Header with Logo */' +
      '.header { display: flex; align-items: center; padding-bottom: 3px; margin-bottom: 3px; border-bottom: 1px solid #dee2e6; }' +
      '.logo { width: 30px; height: 30px; object-fit: contain; flex-shrink: 0; }' +
      '.header-text { flex: 1; text-align: center; min-width: 0; }' +
      '.header-text h2 { margin: 0; font-size: 11px; font-weight: bold; color: #6c757d; letter-spacing: 1px; line-height: 1.1; }' +
      '.header-text p { margin: 1px 0 0 0; font-size: 9px; color: #212529; font-weight: bold; line-height: 1.1; }' +
      '/* Stock ID Box */' +
      '.stock-id-box { border: 1.5px solid #212529; padding: 3px; text-align: center; margin-bottom: 4px; }' +
      '.stock-id-label { font-size: 7px; text-transform: uppercase; letter-spacing: 1px; color: #6c757d; line-height: 1; }' +
      '.stock-id-value { font-size: 15px; font-weight: bold; font-family: monospace; color: #212529; line-height: 1.1; }' +
      '/* Data Table */' +
      '.content-table { width: 100%; border-collapse: collapse; font-size: 9px; table-layout: fixed; }' +
      '.content-table td { padding: 1px 4px; line-height: 1.15; }' +
      '.content-table tr { border-bottom: 1px solid #dee2e6; }' +
      '.content-table tr:last-child { border-bottom: none; }' +
      '.field-label { font-weight: bold; width: 38%; color: #6c757d; text-transform: uppercase; font-size: 7px; }' +
      '.field-value { text-align: right; color: #212529; font-size: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }' +
      '.weight-row { background: #fff3cd !important; }' +
      '.weight-value { font-size: 12px; font-weight: bold; }' +
      '/* QR Section at Bottom */' +
      '.qr-section { margin-top: auto; padding-top: 3px; border-top: 1px solid #dee2e6; text-align: center; }' +
      '.qr-hint { font-size: 6px; color: #6c757d; margin-bottom: 1px; line-height: 1; }' +
      '.qr-container { display: inline-flex; position: relative; align-items: center; justify-content: center; }' +
      '.qr-code-img { width: 60px; height: 60px; }' +
      '/* Footer */' +
      '.footer { text-align: center; font-size: 6px; color: #6c757d; margin-top: 2px; line-height: 1; }' +
      '@media print { ' +
      '  body { margin: 0; padding: 0; background: white; } ' +
      '  .label, .label-small { page-break-after: always; page-break-inside: avoid; background: white; } ' +
      '  .label:last-child, .label-small:last-child { page-break-after: avoid; } ' +
      '}' +
      '</style></head><body>' +
      '<div class="labels-container">' + labelsHTML + '</div>' +
      '<script>' +
      'var imgs = document.querySelectorAll("img");' +
      'var loaded = 0; var total = imgs.length;' +
      'function triggerPrint() { setTimeout(function() { window.focus(); window.print(); }, 500); }' +
      'if (total === 0) { triggerPrint(); } else {' +
      '  imgs.forEach(function(img) {' +
      '    if (img.complete) { loaded++; } else {' +
      '      img.onload = function() { loaded++; if (loaded >= total) triggerPrint(); };' +
      '      img.onerror = function() { loaded++; if (loaded >= total) triggerPrint(); };' +
      '    }' +
      '  });' +
      '  if (loaded >= total) triggerPrint();' +
      '  setTimeout(triggerPrint, 3000);' +
      '}' +
      '</' + 'script></body></html>';

    openPrintWindow(htmlContent);
    
    setLabelSizeDialogOpen(false);
    setIsPrintDialogOpen(false);
    setSelectedForPrint([]);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "available":
        return <span className="px-2 py-1 text-xs rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/50">Available</span>;
      case "stock_used":
      case "used":
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/50">Stock Used</span>;
      case "return":
        return <span className="px-2 py-1 text-xs rounded-full bg-cyan-600/20 text-cyan-400 border border-gray-600/50">Return</span>;
      case "printed":
      case "sent_for_printing":
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/50">Sent for Printing</span>;
      case "sent_for_corrugation":
        return <span className="px-2 py-1 text-xs rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/50">Sent for Corrugation</span>;
      case "received_corrugation":
        return <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/50">Received (Corrugation)</span>;
      case "received":
        return <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/50">Received</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/50">{status}</span>;
    }
  };

  const formatMillDealer = (item) => {
    const mill = item.mill_name || "";
    const dealer = item.dealer_name || "";
    if (mill && dealer) return `${mill} - ${dealer}`;
    return mill || dealer || "-";
  };

  // Filtered materials for RM Name dropdown
  const filteredMaterials = materials.filter(m => 
    !rmNameSearch || 
    m.product_code?.toLowerCase().includes(rmNameSearch.toLowerCase()) ||
    m.product_type?.toLowerCase().includes(rmNameSearch.toLowerCase())
  );

  // Filtered mill names
  const filteredMillNames = uniqueMillNames.filter(m => 
    !millNameSearch || m?.toLowerCase().includes(millNameSearch.toLowerCase())
  );

  // Filtered dealer names
  const filteredDealerNames = uniqueDealerNames.filter(d => 
    !dealerNameSearch || d?.toLowerCase().includes(dealerNameSearch.toLowerCase())
  );

  // Filtered work orders for leftover dialog
  const filteredWorkOrdersLeftover = workOrders.filter(wo => {
    const woNum = wo.header?.work_order_number || wo.wo_number || '';
    const custName = wo.header?.customer_name || wo.customer_name || '';
    const status = wo.status || '';
    return status !== "Delivered" && (
      !woSearchLeftover || 
      woNum.toLowerCase().includes(woSearchLeftover.toLowerCase()) ||
      custName.toLowerCase().includes(woSearchLeftover.toLowerCase())
    );
  });

  // Filtered work orders for bit reel dialog
  const filteredWorkOrdersBitReel = workOrders.filter(wo => {
    const woNum = wo.header?.work_order_number || wo.wo_number || '';
    const custName = wo.header?.customer_name || wo.customer_name || '';
    return !woSearchBitReel || 
      woNum.toLowerCase().includes(woSearchBitReel.toLowerCase()) ||
      custName.toLowerCase().includes(woSearchBitReel.toLowerCase());
  });

  // WO-scoped reel IDs: collect all reel IDs mentioned in the selected work order
  // (P1 base/flute reels, P2A reels, P2B printing paper, plus any single reel_no
  // fields). The Parent Reel dropdown is then filtered to only these IDs so the
  // operator can't accidentally pick a reel from another work order.
  const woScopedReelIds = useMemo(() => {
    if (!leftoverData.workOrderNumber) return null;  // null = "no WO selected, don't filter"
    const wo = workOrders.find(w => (w.header?.work_order_number || w.wo_number) === leftoverData.workOrderNumber);
    if (!wo) return new Set();
    const ids = new Set();
    const proc = wo.processes || {};
    // P1 — base & flute reels (corrugation)
    (proc.process1?.rows || []).forEach(r => {
      if (r?.base_reel_id) ids.add(String(r.base_reel_id).trim());
      if (r?.flute_reel_id) ids.add(String(r.flute_reel_id).trim());
    });
    // P2A / P2B — sheet cutting & paper cutting
    ["process2a", "process2b"].forEach(k => {
      (proc[k]?.reels || []).forEach(r => {
        if (r?.reel_no) ids.add(String(r.reel_no).trim());
      });
      if (proc[k]?.reel_no) ids.add(String(proc[k].reel_no).trim());
    });
    // P3 — printing/pasting paper reel
    if (proc.process3?.stock_id) ids.add(String(proc.process3.stock_id).trim());
    if (proc.process3?.reel_no) ids.add(String(proc.process3.reel_no).trim());
    return ids;
  }, [leftoverData.workOrderNumber, workOrders]);

  // Filtered available inventory for parent reel selection (only available items).
  // When a WO is selected, also restrict to reels referenced in that WO.
  const filteredAvailableReels = inventory.filter(inv => {
    if (inv.status !== "available") return false;
    if (woScopedReelIds && !woScopedReelIds.has(String(inv.reel_id || "").trim())) return false;
    if (!parentReelSearch) return true;
    return (
      inv.reel_id?.toLowerCase().includes(parentReelSearch.toLowerCase()) ||
      inv.rm_name?.toLowerCase().includes(parentReelSearch.toLowerCase())
    );
  });

  // Filtered available inventory for Add Bit Reel parent selection
  const filteredAddBitReelParents = inventory.filter(inv => 
    inv.status === "available" && !inv.is_bit_reel && (
      !addBitReelParentSearch ||
      inv.reel_id?.toLowerCase().includes(addBitReelParentSearch.toLowerCase()) ||
      inv.rm_name?.toLowerCase().includes(addBitReelParentSearch.toLowerCase())
    )
  );

  // Get last RM name from inventory for auto-population
  const getLastRmName = () => {
    const sortedInventory = [...inventory].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    return sortedInventory[0]?.rm_name || "";
  };

  return (
    <div className="space-y-6">
      {/* Inventory Mode Toggle */}
      <div className="flex items-center gap-2 bg-gray-800/50 border border-gray-700 rounded-lg p-1 w-fit">
        <Button
          variant={inventoryMode === "raw_materials" ? "default" : "ghost"}
          size="sm"
          onClick={() => setInventoryMode("raw_materials")}
          className={inventoryMode === "raw_materials" ? "bg-cyan-600 text-white" : "text-gray-400 hover:text-white"}
          data-testid="toggle-raw-materials"
        >
          <Boxes className="h-4 w-4 mr-2" />
          Raw Materials
        </Button>
        <Button
          variant={inventoryMode === "machine_inventory" ? "default" : "ghost"}
          size="sm"
          onClick={() => setInventoryMode("machine_inventory")}
          className={inventoryMode === "machine_inventory" ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"}
          data-testid="toggle-machine-inventory"
        >
          <Wrench className="h-4 w-4 mr-2" />
          Machine Inventory
        </Button>
        <Button
          variant={inventoryMode === "consumption" ? "default" : "ghost"}
          size="sm"
          onClick={() => setInventoryMode("consumption")}
          className={inventoryMode === "consumption" ? "bg-emerald-600 text-white" : "text-gray-400 hover:text-white"}
          data-testid="toggle-consumption-inventory"
        >
          <Layers className="h-4 w-4 mr-2" />
          Consumption
        </Button>
      </div>

      {inventoryMode === "consumption" ? (
        <ConsumptionInventory />
      ) : inventoryMode === "machine_inventory" ? (
        <MachineInventoryView />
      ) : (
      <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-cyan-400 tracking-wider">
            Inventory
          </h1>
          <p className="text-gray-400 mt-1">Manage paper reels and stock</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
          <Button
            onClick={openPrintDialog}
            variant="outline"
            className={cn(
              "border-pink-500/50 text-pink-400 hover:bg-pink-500/20",
              selectedForPrint.length > 0 && "bg-pink-500/20"
            )}
            data-testid="print-labels-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Labels {selectedForPrint.length > 0 && `(${selectedForPrint.length})`}
          </Button>
          <Button
            onClick={() => setIsExportDialogOpen(true)}
            variant="outline"
            className="border-gray-600/50 text-cyan-400 hover:bg-cyan-600/20"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={openBulkAddDialog}
            variant="outline"
            className="border-gray-600/50 text-cyan-400 hover:bg-purple-500/20"
            data-testid="bulk-add-btn"
          >
            <Copy className="h-4 w-4 mr-2" />
            Bulk Add
          </Button>
          <Button 
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            onClick={() => {
              setLeftoverData({
                workOrderNumber: "",
                materialType: "bit_reel",
                parentReelId: "",
                weight: "",
                quantity: "",
                rmName: "",
                reelSize: "",
                comment: ""
              });
              setLeftoverDialogOpen(true);
            }}
            data-testid="record-leftover-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            WO Leftover
          </Button>
          <Button 
            variant="outline"
            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
            onClick={() => {
              resetForm();
              const lastRm = getLastRmName();
              setFormData(prev => ({ 
                ...prev, 
                is_bit_reel: true, 
                reel_id: prev.reel_id + "-BIT",
                rm_name: lastRm, // Auto-populate from last RM
                unit: "Kgs", // Default unit to Kgs for bit reels
                product: "Kraft Paper" // Default product type
              }));
              setIsAddDialogOpen(true);
            }}
            data-testid="add-bit-reel-btn"
          >
            <Scissors className="h-4 w-4 mr-2" />
            Add Bit Reel
          </Button>
          <Button 
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
            onClick={() => setIsArchiveDialogOpen(true)}
          >
            <Archive className="h-4 w-4 mr-2" />
            Archive
          </Button>
          <Button 
            variant="outline"
            className="border-red-600/50 text-red-500 hover:bg-red-600/20"
            onClick={() => setIsDeleteAllDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete All
          </Button>
          <Button className="bg-cyan-500 hover:bg-emerald-600 text-black font-bold" onClick={openAddDialog} data-testid="add-stock-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Stock
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-black/60 border-2 border-cyan-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-cyan-500/20 rounded-lg">
              <CheckCircle className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Available Stock</p>
              <p className="text-2xl font-bold text-cyan-400">{availableCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-gray-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-gray-500/20 rounded-lg">
              <Clock className="h-6 w-6 text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Stock Used</p>
              <p className="text-2xl font-bold text-gray-400">{usedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-purple-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <Printer className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Sent for Printing</p>
              <p className="text-2xl font-bold text-purple-400">{printedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-orange-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <Layers className="h-6 w-6 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Sent for Corrugation</p>
              <p className="text-2xl font-bold text-orange-400">{corrugationCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-gray-600/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-cyan-600/20 rounded-lg">
              <RotateCcw className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Return</p>
              <p className="text-2xl font-bold text-cyan-400">{returnCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Boxes className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Weight (Available)</p>
              <p className="text-2xl font-bold text-blue-400">{totalWeight.toLocaleString()} kg</p>
            </div>
          </CardContent>
        </Card>
        {agingCount > 0 && (
        <Card 
          className={cn(
            "bg-black/60 border-2 cursor-pointer transition-all hover:scale-105",
            filterStatus === "aging" ? "border-red-500 ring-2 ring-red-500/30" : "border-red-500/50 animate-pulse"
          )}
          onClick={() => setFilterStatus(filterStatus === "aging" ? "available" : "aging")}
          data-testid="aging-stock-card"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-500/20 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-red-300">Aging Stock (&gt;60 days)</p>
              <p className="text-2xl font-bold text-red-400">{agingCount}</p>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Search and Filters */}
      {/* Status Filter Buttons */}
      <div className="flex flex-wrap gap-2" data-testid="status-filter-buttons">
        <Button
          variant={filterStatus === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("all")}
          className={filterStatus === "all" 
            ? "bg-cyan-500 text-black font-semibold hover:bg-cyan-400" 
            : "border-gray-600 text-gray-400 hover:text-white hover:border-gray-400"}
          data-testid="filter-btn-all"
        >
          All ({inventory.length})
        </Button>
        <Button
          variant={filterStatus === "available" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("available")}
          className={filterStatus === "available" 
            ? "bg-emerald-500 text-black font-semibold hover:bg-emerald-400" 
            : "border-emerald-500/40 text-emerald-400 hover:text-emerald-300 hover:border-emerald-400"}
          data-testid="filter-btn-available"
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
          Available Stock ({availableCount})
        </Button>
        <Button
          variant={filterStatus === "stock_used" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("stock_used")}
          className={filterStatus === "stock_used" 
            ? "bg-gray-500 text-black font-semibold hover:bg-gray-400" 
            : "border-gray-500/40 text-gray-400 hover:text-gray-300 hover:border-gray-400"}
          data-testid="filter-btn-stock-used"
        >
          <Clock className="h-3.5 w-3.5 mr-1.5" />
          Stock Used ({usedCount})
        </Button>
        <Button
          variant={filterStatus === "printed" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("printed")}
          className={filterStatus === "printed" 
            ? "bg-purple-500 text-black font-semibold hover:bg-purple-400" 
            : "border-purple-500/40 text-purple-400 hover:text-purple-300 hover:border-purple-400"}
          data-testid="filter-btn-printed"
        >
          <Printer className="h-3.5 w-3.5 mr-1.5" />
          Sent for Printing ({printedCount})
        </Button>
        <Button
          variant={filterStatus === "sent_for_corrugation" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus("sent_for_corrugation")}
          className={filterStatus === "sent_for_corrugation" 
            ? "bg-orange-500 text-black font-semibold hover:bg-orange-400" 
            : "border-orange-500/40 text-orange-400 hover:text-orange-300 hover:border-orange-400"}
          data-testid="filter-btn-corrugation"
        >
          <Layers className="h-3.5 w-3.5 mr-1.5" />
          Sent for Corrugation ({corrugationCount})
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="bg-black/60 border-2 border-cyan-500/30">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex gap-2 flex-1 flex-wrap">
              <Select value={searchField} onValueChange={setSearchField}>
                <SelectTrigger className="w-[130px] bg-black/50 border-cyan-500/30 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-cyan-500/30">
                  <SelectItem value="all">All Fields</SelectItem>
                  <SelectItem value="size">Size</SelectItem>
                  <SelectItem value="rm_name">RM Name</SelectItem>
                  <SelectItem value="weight">Weight</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                {searchField === 'date' ? (
                  <Input
                    type="date"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-black/50 border-cyan-500/30 text-white [color-scheme:dark]"
                  />
                ) : (
                  <Input
                    placeholder={`Search by ${searchField === 'all' ? 'any field' : searchField}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-black/50 border-cyan-500/30 text-white"
                  />
                )}
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Filter className="h-4 w-4 text-cyan-400" />
              <Select value={filterProduct} onValueChange={setFilterProduct}>
                <SelectTrigger className="w-[150px] bg-black/50 border-cyan-500/30 text-white">
                  <SelectValue placeholder="Product" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-cyan-500/30">
                  <SelectItem value="all">All Products</SelectItem>
                  {productTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(searchTerm || filterProduct !== "all" || filterStatus !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSearchTerm(""); setFilterProduct("all"); setFilterStatus("all"); }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aging Stock Alert Banner */}
      {agingCount > 0 && (filterStatus === "all" || filterStatus === "available" || filterStatus === "aging") && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-red-500/40 bg-red-500/10" data-testid="aging-stock-alert">
          <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
          <div className="flex-1">
            <p className="text-red-300 font-semibold text-sm">
              {agingCount} reel{agingCount > 1 ? 's' : ''} in stock for more than 60 days
            </p>
            <p className="text-red-400/70 text-xs mt-0.5">
              Aging stock: {agingItems.slice(0, 5).map(i => i.reel_id).join(', ')}{agingCount > 5 ? ` and ${agingCount - 5} more` : ''}
              {' '}&mdash; Total weight: {agingItems.reduce((sum, i) => sum + (i.reel_weight || 0), 0).toLocaleString()} kg
            </p>
          </div>
        </div>
      )}

      {/* Available Stock Table */}
      {(filterStatus === "all" || filterStatus === "available" || filterStatus === "aging") && (
      <Card className="bg-black/60 border-2 border-cyan-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-cyan-400 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Available Stock ({displayAvailableInventory.length} items{filterStatus === "aging" ? " - Aging >60 days" : ""})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading inventory...</div>
          ) : displayAvailableInventory.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {inventory.length === 0 ? (
                <div>
                  <Boxes className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                  <p>No inventory items. Click "Add Stock" to create new entries.</p>
                </div>
              ) : (
                <p>No available stock items match your search criteria.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-cyan-500/30 hover:bg-transparent">
                    <TableHead className="text-cyan-300 w-10">
                      <Checkbox 
                        checked={selectedForPrint.length === displayAvailableInventory.length && displayAvailableInventory.length > 0}
                        onCheckedChange={() => {
                          const availableIds = displayAvailableInventory.map(i => i.id);
                          if (selectedForPrint.filter(id => availableIds.includes(id)).length === displayAvailableInventory.length) {
                            setSelectedForPrint(prev => prev.filter(id => !availableIds.includes(id)));
                          } else {
                            setSelectedForPrint(prev => [...new Set([...prev, ...availableIds])]);
                          }
                        }}
                        className="border-cyan-500/50"
                      />
                    </TableHead>
                    <TableHead className="text-cyan-300">Stock ID</TableHead>
                    <TableHead className="text-cyan-300">In Date</TableHead>
                    <TableHead className="text-cyan-300">Product</TableHead>
                    <TableHead className="text-cyan-300">RM Name</TableHead>
                    <TableHead className="text-cyan-300">Size</TableHead>
                    <TableHead className="text-cyan-300">Weight</TableHead>
                    <TableHead className="text-cyan-300">Qty</TableHead>
                    <TableHead className="text-cyan-300">Mill - Dealer</TableHead>
                    <TableHead className="text-cyan-300">Age</TableHead>
                    <TableHead className="text-cyan-300">Status</TableHead>
                    <TableHead className="text-cyan-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayAvailableInventory.map((item) => {
                    const daysAge = getDaysInInventory(item.in_date);
                    const isAging = daysAge > AGING_THRESHOLD;
                    return (
                    <TableRow
                      key={item.id}
                      className={cn(
                        "border-cyan-500/20 hover:bg-cyan-500/10",
                        selectedForPrint.includes(item.id) && "bg-pink-500/10",
                        isAging && "bg-red-500/8 border-l-2 border-l-red-500/60"
                      )}
                    >
                      <TableCell>
                        <Checkbox 
                          checked={selectedForPrint.includes(item.id)}
                          onCheckedChange={() => toggleSelectForPrint(item.id)}
                          className="border-pink-500/50"
                        />
                      </TableCell>
                      <TableCell className="font-mono font-bold text-white">
                        <div className="flex items-center gap-2">
                          {item.reel_id}
                          {item.is_bit_reel && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/50">
                              BIT
                            </span>
                          )}
                        </div>
                        {item.parent_reel_id && (
                          <p className="text-[10px] text-gray-500">
                            From: {item.parent_reel_id}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.in_date || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.product || "-"}
                      </TableCell>
                      <TableCell className="text-cyan-300 font-medium">
                        {item.rm_name || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.reel_size ? `${item.reel_size}"` : "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.reel_weight ? `${item.reel_weight} kg` : "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.qty || 1}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {formatMillDealer(item)}
                      </TableCell>
                      <TableCell>
                        {daysAge > 0 ? (
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            daysAge > AGING_THRESHOLD 
                              ? "bg-red-500/20 text-red-400 border border-red-500/40" 
                              : daysAge > 30 
                                ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                                : "bg-green-500/20 text-green-400 border border-green-500/40"
                          )}>
                            {daysAge}d
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={item.status || "available"}
                          onValueChange={(v) => handleStatusChange(item, v)}
                        >
                          <SelectTrigger className="w-[130px] h-8 bg-transparent border-none p-0">
                            {getStatusBadge(item.status)}
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-cyan-500/30 z-50">
                            <SelectItem value="available">✓ Available</SelectItem>
                            <SelectItem value="stock_used">📦 Stock Used</SelectItem>
                            <SelectItem value="bit_reel">✂️ Partial Use (Bit Reel)</SelectItem>
                            <SelectItem value="return">↩ Return</SelectItem>
                          </SelectContent>
                        </Select>
                        {item.used_comment && (
                          <p className="text-xs text-gray-500 italic mt-1 truncate max-w-[120px]" title={item.used_comment}>
                            "{item.used_comment}"
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openViewDialog(item)}
                            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(item)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(item)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Used Stock Table */}
      {(filterStatus === "all" || filterStatus === "stock_used") && (
      <Card className="bg-black/60 border-2 border-gray-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-gray-400 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Stock Used ({usedInventory.length} items)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : usedInventory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-600" />
              <p>No used stock items.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-500/30 hover:bg-transparent">
                    <TableHead className="text-gray-400">Stock ID</TableHead>
                    <TableHead className="text-gray-400">In Date</TableHead>
                    <TableHead className="text-gray-400">Product</TableHead>
                    <TableHead className="text-gray-400">RM Name</TableHead>
                    <TableHead className="text-gray-400">Size</TableHead>
                    <TableHead className="text-gray-400">Weight</TableHead>
                    <TableHead className="text-gray-400">Qty</TableHead>
                    <TableHead className="text-gray-400">Used In WO</TableHead>
                    <TableHead className="text-gray-400">Comment</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usedInventory.map((item) => (
                    <TableRow
                      key={item.id}
                      className="border-gray-500/20 hover:bg-gray-500/10"
                    >
                      <TableCell className="font-mono text-gray-300">
                        <div className="flex items-center gap-2">
                          {item.reel_id}
                          {item.is_bit_reel && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-500/20 text-gray-400 border border-gray-500/50">
                              BIT
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {item.in_date || "-"}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {item.product || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.rm_name || "-"}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {item.reel_size ? `${item.reel_size}"` : "-"}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {item.reel_weight ? `${item.reel_weight} kg` : "-"}
                      </TableCell>
                      <TableCell className="text-cyan-400 font-medium">
                        {item.qty ? `${item.qty} ${item.unit || 'Nos'}` : "-"}
                      </TableCell>
                      <TableCell className="text-amber-400 font-medium">
                        {item.used_in_work_order || "-"}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm max-w-[150px] truncate" title={item.used_comment}>
                        {item.used_comment || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openViewDialog(item)}
                            className="text-gray-400 hover:text-gray-300 hover:bg-gray-500/20"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setItemToRestore(item);
                              setRestorePassword("");
                              setIsRestoreDialogOpen(true);
                            }}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                            title="Restore to Available (Password Protected)"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Sent for Printing Table */}
      {(filterStatus === "all" || filterStatus === "printed") && (
      <Card className="bg-black/60 border-2 border-purple-500/30" data-testid="printed-stock-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-purple-400 flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Sent for Printing ({printedInventory.length} items)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : printedInventory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Printer className="h-12 w-12 mx-auto mb-4 text-gray-600" />
              <p>No items sent for printing.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-purple-500/30 hover:bg-transparent">
                    <TableHead className="text-purple-300">Stock ID</TableHead>
                    <TableHead className="text-purple-300">In Date</TableHead>
                    <TableHead className="text-purple-300">Product</TableHead>
                    <TableHead className="text-purple-300">RM Name</TableHead>
                    <TableHead className="text-purple-300">Size</TableHead>
                    <TableHead className="text-purple-300">Weight</TableHead>
                    <TableHead className="text-purple-300">Qty</TableHead>
                    <TableHead className="text-purple-300">Comment</TableHead>
                    <TableHead className="text-purple-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {printedInventory.map((item) => (
                    <TableRow key={item.id} className="border-purple-500/20 hover:bg-purple-500/10">
                      <TableCell className="font-mono text-purple-300">{item.reel_id}</TableCell>
                      <TableCell className="text-gray-400">{item.in_date || "-"}</TableCell>
                      <TableCell className="text-gray-400">{item.product || "-"}</TableCell>
                      <TableCell className="text-gray-300">{item.rm_name || "-"}</TableCell>
                      <TableCell className="text-gray-400">{item.reel_size ? `${item.reel_size}"` : "-"}</TableCell>
                      <TableCell className="text-gray-400">{item.reel_weight ? `${item.reel_weight} kg` : "-"}</TableCell>
                      <TableCell className="text-purple-400 font-medium">{item.qty ? `${item.qty} ${item.unit || 'Nos'}` : "-"}</TableCell>
                      <TableCell className="text-gray-500 text-sm max-w-[150px] truncate" title={item.used_comment}>{item.used_comment || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openViewDialog(item)}
                            className="text-gray-400 hover:text-gray-300 hover:bg-gray-500/20"
                            title="View Details"
                            data-testid={`view-printed-${item.reel_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setItemToRestore(item);
                              setRestorePassword("");
                              setIsRestoreDialogOpen(true);
                            }}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                            title="Restore to Available"
                            data-testid={`restore-printed-${item.reel_id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Sent for Corrugation Table */}
      {(filterStatus === "all" || filterStatus === "sent_for_corrugation") && (
      <Card className="bg-black/60 border-2 border-orange-500/30" data-testid="corrugation-stock-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-orange-400 flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Sent for Corrugation ({corrugationInventory.length} items)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : corrugationInventory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Layers className="h-12 w-12 mx-auto mb-4 text-gray-600" />
              <p>No items sent for corrugation.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-orange-500/30 hover:bg-transparent">
                    <TableHead className="text-orange-300">Stock ID</TableHead>
                    <TableHead className="text-orange-300">In Date</TableHead>
                    <TableHead className="text-orange-300">Product</TableHead>
                    <TableHead className="text-orange-300">RM Name</TableHead>
                    <TableHead className="text-orange-300">Size</TableHead>
                    <TableHead className="text-orange-300">Weight</TableHead>
                    <TableHead className="text-orange-300">Qty</TableHead>
                    <TableHead className="text-orange-300">Comment</TableHead>
                    <TableHead className="text-orange-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {corrugationInventory.map((item) => (
                    <TableRow key={item.id} className="border-orange-500/20 hover:bg-orange-500/10">
                      <TableCell className="font-mono text-orange-300">{item.reel_id}</TableCell>
                      <TableCell className="text-gray-400">{item.in_date || "-"}</TableCell>
                      <TableCell className="text-gray-400">{item.product || "-"}</TableCell>
                      <TableCell className="text-gray-300">{item.rm_name || "-"}</TableCell>
                      <TableCell className="text-gray-400">{item.reel_size ? `${item.reel_size}"` : "-"}</TableCell>
                      <TableCell className="text-gray-400">{item.reel_weight ? `${item.reel_weight} kg` : "-"}</TableCell>
                      <TableCell className="text-orange-400 font-medium">{item.qty ? `${item.qty} ${item.unit || 'Nos'}` : "-"}</TableCell>
                      <TableCell className="text-gray-500 text-sm max-w-[150px] truncate" title={item.used_comment}>{item.used_comment || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openViewDialog(item)}
                            className="text-gray-400 hover:text-gray-300 hover:bg-gray-500/20"
                            title="View Details"
                            data-testid={`view-corrugation-${item.reel_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setItemToRestore(item);
                              setRestorePassword("");
                              setIsRestoreDialogOpen(true);
                            }}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                            title="Restore to Available"
                            data-testid={`restore-corrugation-${item.reel_id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Add Stock Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={formData.is_bit_reel ? "text-cyan-400 text-xl" : "text-cyan-400 text-xl"}>
              {formData.is_bit_reel ? "✂️ Add Bit Reel" : "Add New Stock"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Common Fields: In Date and Product Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-cyan-300">In Date *</Label>
                <Input
                  type="date"
                  value={formData.in_date}
                  onChange={(e) => setFormData({ ...formData, in_date: e.target.value })}
                  className="bg-black/50 border-cyan-500/30 text-white mt-1 [color-scheme:dark]"
                />
              </div>
              <div>
                <Label className="text-cyan-300">Product Type *</Label>
                <Select value={formData.product} onValueChange={(v) => setFormData({ ...formData, product: v })}>
                  <SelectTrigger className="bg-black/50 border-cyan-500/30 text-white mt-1">
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-cyan-500/30 max-h-60">
                    {allProductTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                        {PAPER_TYPES.includes(type) && <span className="ml-2 text-xs text-cyan-400">(Paper)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Conditional Fields based on Product Type */}
            {isPaperProduct(formData.product) ? (
              /* Paper Product Fields */
              <>
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-between">
                  <p className="text-cyan-300 text-sm font-medium">Paper Product Fields</p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_bit_reel"
                      checked={formData.is_bit_reel}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_bit_reel: checked })}
                      className="border-cyan-500 data-[state=checked]:bg-cyan-500"
                    />
                    <Label htmlFor="is_bit_reel" className="text-cyan-400 text-sm cursor-pointer">
                      ✂️ This is a Bit Reel
                    </Label>
                  </div>
                </div>

                {/* Bit Reel Info Fields */}
                {formData.is_bit_reel && (
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <p className="text-cyan-300 text-sm font-medium mb-3">Bit Reel Information</p>
                    <div className="space-y-3">
                      {/* Parent Reel ID - Searchable Dropdown */}
                      <div>
                        <Label className="text-cyan-300 text-sm">Parent Reel ID (Select from Available Stock)</Label>
                        <Popover open={addBitReelParentOpen} onOpenChange={setAddBitReelParentOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={addBitReelParentOpen}
                              className="w-full justify-between bg-black/50 border-cyan-500/30 text-white mt-1 hover:bg-black/70"
                            >
                              {formData.parent_reel_id || "Search Parent Reel..."}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[450px] p-0 bg-gray-900 border-cyan-500/30">
                            <Command className="bg-gray-900" shouldFilter={false}>
                              <CommandInput 
                                placeholder="Search by Reel ID or RM Name..." 
                                value={addBitReelParentSearch}
                                onValueChange={setAddBitReelParentSearch}
                                className="bg-gray-900 text-white"
                              />
                              <CommandList className="max-h-60 overflow-y-auto">
                                <CommandEmpty>No available reels found.</CommandEmpty>
                                <CommandGroup>
                                  {filteredAddBitReelParents.map((inv) => (
                                    <CommandItem
                                      key={inv.id}
                                      value={inv.reel_id}
                                      onSelect={() => {
                                        // Auto-populate all fields from parent
                                        setFormData(prev => ({
                                          ...prev,
                                          parent_reel_id: inv.reel_id,
                                          original_weight: inv.reel_weight?.toString() || "",
                                          reel_size: inv.reel_size?.toString() || "",
                                          rm_name: inv.rm_name || "",
                                          paper_price: inv.paper_price?.toString() || "",
                                          mill_reel_no: inv.mill_reel_no || "",
                                          mill_name: inv.mill_name || "",
                                          dealer_name: inv.dealer_name || "",
                                          product: inv.product || "Kraft Paper"
                                        }));
                                        setAddBitReelParentOpen(false);
                                      }}
                                      className="text-white hover:bg-cyan-500/20 cursor-pointer"
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          formData.parent_reel_id === inv.reel_id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex flex-col">
                                        <span className="font-bold">{inv.reel_id}</span>
                                        <span className="text-xs text-gray-400">
                                          {inv.rm_name || "N/A"} | Size: {inv.reel_size || "N/A"}" | {inv.reel_weight || 0} Kg | {inv.mill_name || "N/A"}
                                        </span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      {/* Auto-populated fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-cyan-300 text-sm">Original Weight (Kg)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.original_weight}
                            onChange={(e) => setFormData({ ...formData, original_weight: e.target.value })}
                            className="bg-black/50 border-cyan-500/30 text-white mt-1"
                            placeholder="Auto-filled from parent"
                          />
                        </div>
                        <div>
                          <Label className="text-cyan-300 text-sm">Reel Size (Auto)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.reel_size}
                            onChange={(e) => setFormData({ ...formData, reel_size: e.target.value })}
                            className="bg-black/50 border-cyan-500/30 text-white mt-1"
                            placeholder="Auto-filled"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-cyan-300 text-sm">Paper Price (Auto)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.paper_price}
                            onChange={(e) => setFormData({ ...formData, paper_price: e.target.value })}
                            className="bg-black/50 border-cyan-500/30 text-white mt-1"
                            placeholder="Auto-filled"
                          />
                        </div>
                        <div>
                          <Label className="text-cyan-300 text-sm">Mill Reel No (Auto)</Label>
                          <Input
                            value={formData.mill_reel_no}
                            onChange={(e) => setFormData({ ...formData, mill_reel_no: e.target.value })}
                            className="bg-black/50 border-cyan-500/30 text-white mt-1"
                            placeholder="Auto-filled"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-cyan-300 text-sm">Mill Name (Auto)</Label>
                          <Input
                            value={formData.mill_name}
                            onChange={(e) => setFormData({ ...formData, mill_name: e.target.value })}
                            className="bg-black/50 border-cyan-500/30 text-white mt-1"
                            placeholder="Auto-filled"
                          />
                        </div>
                        <div>
                          <Label className="text-cyan-300 text-sm">Dealer Name (Auto)</Label>
                          <Input
                            value={formData.dealer_name}
                            onChange={(e) => setFormData({ ...formData, dealer_name: e.target.value })}
                            className="bg-black/50 border-cyan-500/30 text-white mt-1"
                            placeholder="Auto-filled"
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Select a parent reel to auto-populate fields. Bit reels are partial reels created from leftover material.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-cyan-300">Stock ID *</Label>
                    <Input
                      value={formData.reel_id}
                      onChange={(e) => setFormData({ ...formData, reel_id: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                      placeholder={nextReelId}
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: RMCPI###</p>
                  </div>
                  <div>
                    <Label className="text-cyan-300">Reel Size (inches)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.reel_size}
                      onChange={(e) => setFormData({ ...formData, reel_size: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Reel Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.reel_weight}
                      onChange={(e) => setFormData({ ...formData, reel_weight: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                </div>

                {/* Shade, GSM, BF -> RM Name Auto-generation */}
                {!formData.is_bit_reel && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <p className="text-emerald-300 text-sm font-medium mb-3">Generate RM Name from Shade, GSM & BF</p>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-emerald-300 text-xs">Shade *</Label>
                        <Select value={formData.shade || undefined} onValueChange={(v) => handleFormDataShadeGsmBf('shade', v)}>
                          <SelectTrigger className="bg-black/50 border-emerald-500/30 text-white mt-1">
                            <SelectValue placeholder="Select Shade" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-emerald-500/30">
                            {SHADE_OPTIONS.map(shade => (
                              <SelectItem key={shade} value={shade} className="text-white hover:bg-emerald-500/20">
                                {shade}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-emerald-300 text-xs">GSM *</Label>
                        <Input
                          type="number"
                          value={formData.gsm}
                          onChange={(e) => handleFormDataShadeGsmBf('gsm', e.target.value)}
                          className="bg-black/50 border-emerald-500/30 text-white mt-1"
                          placeholder="e.g., 150"
                        />
                      </div>
                      <div>
                        <Label className="text-emerald-300 text-xs">BF *</Label>
                        <Input
                          type="number"
                          value={formData.bf}
                          onChange={(e) => handleFormDataShadeGsmBf('bf', e.target.value)}
                          className="bg-black/50 border-emerald-500/30 text-white mt-1"
                          placeholder="e.g., 18"
                        />
                      </div>
                      <div>
                        <Label className="text-cyan-300 text-xs">RM Name (Auto)</Label>
                        <Input
                          value={formData.rm_name}
                          readOnly
                          className="bg-cyan-500/10 border-cyan-500/30 text-cyan-300 mt-1 font-mono font-bold"
                          placeholder="Auto-generated"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Or select existing RM Name below</p>
                  </div>
                )}
                  
                {/* RM Name - Simple input for Bit Reel, Searchable for regular */}
                {formData.is_bit_reel ? (
                  <div>
                    <Label className="text-cyan-300">RM Name (Auto-populated)</Label>
                    <Input
                      value={formData.rm_name}
                      onChange={(e) => setFormData({ ...formData, rm_name: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                      placeholder="From last used material"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-filled from last entry</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-cyan-300">Or Select Existing RM Name</Label>
                    <Popover open={rmNameOpen} onOpenChange={setRmNameOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={rmNameOpen}
                          className="w-full justify-between bg-black/50 border-cyan-500/30 text-white mt-1 hover:bg-black/70"
                        >
                          {formData.rm_name || "Search RM Name..."}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0 bg-gray-900 border-cyan-500/30">
                        <Command className="bg-gray-900" shouldFilter={false}>
                          <CommandInput 
                            placeholder="Search RM Name..." 
                            value={rmNameSearch}
                            onValueChange={setRmNameSearch}
                            className="bg-gray-900 text-white"
                          />
                          <CommandList>
                            <CommandEmpty>No materials found.</CommandEmpty>
                            <CommandGroup className="max-h-60 overflow-auto">
                              {filteredMaterials.map((m) => (
                                <CommandItem
                                  key={m.id}
                                  value={m.product_code}
                                  onSelect={() => handleRMNameSelect(m.product_code)}
                                  className="text-white hover:bg-cyan-500/20 cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.rm_name === m.product_code ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {m.product_code} - {m.product_type}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-cyan-300">Paper Price (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.paper_price}
                      onChange={(e) => setFormData({ ...formData, paper_price: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Mill Reel No</Label>
                    <Input
                      value={formData.mill_reel_no}
                      onChange={(e) => setFormData({ ...formData, mill_reel_no: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Quantity</Label>
                    <Input
                      type="number"
                      value={formData.qty}
                      onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                      min="1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-cyan-300">Mill Name (Search & Select)</Label>
                    <Popover open={millNameOpen} onOpenChange={setMillNameOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={millNameOpen}
                          className="w-full justify-between bg-black/50 border-cyan-500/30 text-white mt-1 hover:bg-black/70"
                        >
                          {formData.mill_name || "Search Mill..."}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0 bg-gray-900 border-cyan-500/30">
                        <Command className="bg-gray-900">
                          <CommandInput 
                            placeholder="Search or type Mill Name..." 
                            value={millNameSearch}
                            onValueChange={setMillNameSearch}
                            className="bg-gray-900 text-white"
                          />
                          <CommandList>
                            <CommandEmpty>
                              <Button 
                                variant="ghost" 
                                className="w-full text-cyan-400"
                                onClick={() => {
                                  setFormData({ ...formData, mill_name: millNameSearch });
                                  setMillNameOpen(false);
                                }}
                              >
                                Add "{millNameSearch}"
                              </Button>
                            </CommandEmpty>
                            <CommandGroup className="max-h-40 overflow-auto">
                              {filteredMillNames.map((mill) => (
                                <CommandItem
                                  key={mill}
                                  value={mill}
                                  onSelect={() => {
                                    setFormData({ ...formData, mill_name: mill });
                                    setMillNameOpen(false);
                                  }}
                                  className="text-white hover:bg-cyan-500/20 cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.mill_name === mill ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {mill}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-cyan-300">Dealer Name (Search & Select)</Label>
                    <Popover open={dealerNameOpen} onOpenChange={setDealerNameOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={dealerNameOpen}
                          className="w-full justify-between bg-black/50 border-cyan-500/30 text-white mt-1 hover:bg-black/70"
                        >
                          {formData.dealer_name || "Search Dealer..."}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0 bg-gray-900 border-cyan-500/30">
                        <Command className="bg-gray-900">
                          <CommandInput 
                            placeholder="Search or type Dealer Name..." 
                            value={dealerNameSearch}
                            onValueChange={setDealerNameSearch}
                            className="bg-gray-900 text-white"
                          />
                          <CommandList>
                            <CommandEmpty>
                              <Button 
                                variant="ghost" 
                                className="w-full text-cyan-400"
                                onClick={() => {
                                  setFormData({ ...formData, dealer_name: dealerNameSearch });
                                  setDealerNameOpen(false);
                                }}
                              >
                                Add "{dealerNameSearch}"
                              </Button>
                            </CommandEmpty>
                            <CommandGroup className="max-h-40 overflow-auto">
                              {filteredDealerNames.map((dealer) => (
                                <CommandItem
                                  key={dealer}
                                  value={dealer}
                                  onSelect={() => {
                                    setFormData({ ...formData, dealer_name: dealer });
                                    setDealerNameOpen(false);
                                  }}
                                  className="text-white hover:bg-cyan-500/20 cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.dealer_name === dealer ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {dealer}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </>
            ) : formData.product ? (
              /* Non-Paper Product Fields */
              <>
                <div className="p-3 bg-purple-500/10 border border-gray-600/30 rounded-lg">
                  <p className="text-purple-300 text-sm font-medium">Non-Paper Item Fields</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-purple-300">RM Name (Search & Select)</Label>
                    <Popover open={nonPaperRmNameOpen} onOpenChange={setNonPaperRmNameOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={nonPaperRmNameOpen}
                          className="w-full justify-between bg-black/50 border-gray-600/30 text-white mt-1 hover:bg-black/70"
                        >
                          {formData.rm_name || "Search RM Name..."}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0 bg-gray-900 border-gray-600/30">
                        <Command className="bg-gray-900">
                          <CommandInput 
                            placeholder="Search RM Name..." 
                            value={nonPaperRmNameSearch}
                            onValueChange={setNonPaperRmNameSearch}
                            className="bg-gray-900 text-white"
                          />
                          <CommandList>
                            <CommandEmpty>No materials found.</CommandEmpty>
                            <CommandGroup className="max-h-60 overflow-auto">
                              {materials.filter(m => 
                                m.product_type === formData.product &&
                                (!nonPaperRmNameSearch || 
                                  m.product_code?.toLowerCase().includes(nonPaperRmNameSearch.toLowerCase()) ||
                                  m.item_name?.toLowerCase().includes(nonPaperRmNameSearch.toLowerCase())
                                )
                              ).map((m) => (
                                <CommandItem
                                  key={m.id}
                                  value={m.product_code || m.item_name}
                                  onSelect={() => {
                                    setFormData({ ...formData, rm_name: m.product_code || m.item_name });
                                    setNonPaperRmNameOpen(false);
                                  }}
                                  className="text-white hover:bg-purple-500/20 cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.rm_name === (m.product_code || m.item_name) ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {m.product_code || m.item_name} - {m.product_type}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-purple-300">Qty *</Label>
                    <Input
                      type="number"
                      value={formData.qty}
                      onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                      placeholder="e.g., 10"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300">Units *</Label>
                    <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                      <SelectTrigger className="bg-black/50 border-gray-600/30 text-white mt-1">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-600/30 max-h-60">
                        {unitOptions.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-purple-300">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-black/50 border-gray-600/30 text-white mt-1"
                    placeholder="Enter item description..."
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-purple-300">Tax Preference</Label>
                    <Select value={formData.tax_preference} onValueChange={(v) => setFormData({ ...formData, tax_preference: v })}>
                      <SelectTrigger className="bg-black/50 border-gray-600/30 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-600/30">
                        <SelectItem value="taxable">Taxable</SelectItem>
                        <SelectItem value="non_taxable">Non-Taxable</SelectItem>
                        <SelectItem value="exempt">Exempt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-300">CGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.cgst_rate}
                      onChange={(e) => setFormData({ ...formData, cgst_rate: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                      disabled={formData.tax_preference !== "taxable"}
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300">SGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.sgst_rate}
                      onChange={(e) => setFormData({ ...formData, sgst_rate: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                      disabled={formData.tax_preference !== "taxable"}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400 border border-dashed border-gray-600 rounded-lg">
                <p>Please select a Product Type to see relevant fields</p>
              </div>
            )}
            
            {/* DC Creation Option */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="create_dc"
                  checked={createDC}
                  onCheckedChange={(checked) => setCreateDC(checked)}
                  className="border-amber-500 data-[state=checked]:bg-amber-500"
                />
                <Label htmlFor="create_dc" className="text-amber-400 font-medium cursor-pointer">
                  📄 Create Delivery Challan (DC)
                </Label>
              </div>
              
              {createDC && (
                <div className="space-y-3 pt-3 border-t border-amber-500/30">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-amber-300 text-sm">Vendor/Dealer Name</Label>
                      <Select value={dcData.vendor_name} onValueChange={(v) => setDcData({ ...dcData, vendor_name: v })}>
                        <SelectTrigger className="bg-black/50 border-amber-500/30 text-white mt-1">
                          <SelectValue placeholder="Select vendor..." />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-amber-500/30 max-h-60">
                          {vendors.map(v => (
                            <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-amber-300 text-sm">Vehicle No.</Label>
                      <Input
                        value={dcData.vehicle_no}
                        onChange={(e) => setDcData({ ...dcData, vehicle_no: e.target.value })}
                        placeholder="e.g., TN01AB1234"
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-amber-300 text-sm">Driver Name</Label>
                      <Input
                        value={dcData.driver_name}
                        onChange={(e) => setDcData({ ...dcData, driver_name: e.target.value })}
                        placeholder="Driver name"
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-amber-300 text-sm">Remarks</Label>
                      <Input
                        value={dcData.remarks}
                        onChange={(e) => setDcData({ ...dcData, remarks: e.target.value })}
                        placeholder="Any remarks..."
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-amber-300 text-sm">Manual Item Details (Optional)</Label>
                    <Textarea
                      value={dcData.manual_items}
                      onChange={(e) => setDcData({ ...dcData, manual_items: e.target.value })}
                      placeholder="Enter additional item details manually if needed..."
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleAddItem} className="bg-cyan-500 hover:bg-emerald-600 text-black font-bold">
              Add Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Dialog - Redesigned */}
      <Dialog open={isBulkAddDialogOpen} onOpenChange={setIsBulkAddDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-600/50 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Bulk Add Stock
            </DialogTitle>
            <p className="text-sm text-gray-400 mt-1">Add multiple inventory items at once</p>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {bulkEntries.map((entry, idx) => (
              <div key={idx} className="p-4 bg-black/40 border border-gray-600/30 rounded-lg space-y-4">
                {/* Row Header */}
                <div className="flex justify-between items-center border-b border-gray-600/20 pb-2">
                  <span className="text-purple-300 font-semibold">Item #{idx + 1}</span>
                  {bulkEntries.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeBulkRow(idx)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
                
                {/* Row 1: Basic Info */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Reel ID *</Label>
                    <Input
                      value={entry.reel_id}
                      onChange={(e) => updateBulkEntry(idx, 'reel_id', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      placeholder="RMCPI001"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">In Date *</Label>
                    <Input
                      type="date"
                      value={entry.in_date}
                      onChange={(e) => updateBulkEntry(idx, 'in_date', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1 [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Reel Size (inches)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={entry.reel_size}
                      onChange={(e) => updateBulkEntry(idx, 'reel_size', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      placeholder="e.g., 28"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Product Type</Label>
                    <Input
                      value={entry.product}
                      onChange={(e) => updateBulkEntry(idx, 'product', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      placeholder="Auto-filled"
                    />
                  </div>
                </div>

                {/* Row 2: Shade, GSM, BF -> RM Name Auto-generation */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-emerald-300 text-xs font-medium">Shade *</Label>
                    <Select value={entry.shade || undefined} onValueChange={(v) => updateBulkEntry(idx, 'shade', v)}>
                      <SelectTrigger className="bg-black/50 border-emerald-500/30 text-white h-9 mt-1">
                        <SelectValue placeholder="Select Shade" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-emerald-500/30">
                        {SHADE_OPTIONS.map(shade => (
                          <SelectItem key={shade} value={shade} className="text-white hover:bg-emerald-500/20">
                            {shade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-emerald-300 text-xs font-medium">GSM *</Label>
                    <Input
                      type="number"
                      value={entry.gsm}
                      onChange={(e) => updateBulkEntry(idx, 'gsm', e.target.value)}
                      className="bg-black/50 border-emerald-500/30 text-white h-9 mt-1"
                      placeholder="e.g., 150"
                    />
                  </div>
                  <div>
                    <Label className="text-emerald-300 text-xs font-medium">BF *</Label>
                    <Input
                      type="number"
                      value={entry.bf}
                      onChange={(e) => updateBulkEntry(idx, 'bf', e.target.value)}
                      className="bg-black/50 border-emerald-500/30 text-white h-9 mt-1"
                      placeholder="e.g., 18"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300 text-xs font-medium">RM Name (Auto)</Label>
                    <Input
                      value={entry.rm_name}
                      readOnly
                      className="bg-cyan-500/10 border-cyan-500/30 text-cyan-300 h-9 mt-1 font-mono font-bold"
                      placeholder="Auto-generated"
                    />
                  </div>
                </div>
                
                {/* OR select from existing materials */}
                <div className="border-t border-gray-600/20 pt-3">
                  <Label className="text-gray-400 text-xs">Or select existing RM Name:</Label>
                  <Popover open={bulkRmNameOpenIdx === idx} onOpenChange={(open) => {
                    setBulkRmNameOpenIdx(open ? idx : null);
                    if (!open) setBulkRmNameSearch("");
                  }}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full h-9 justify-between bg-black/50 border-gray-600/30 text-white mt-1 hover:bg-black/70 px-3"
                      >
                        <span className="truncate text-sm">{entry.rm_name || "Select existing RM..."}</span>
                        <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0 bg-gray-900 border-gray-600/30" align="start">
                      <Command className="bg-gray-900" shouldFilter={false}>
                        <CommandInput 
                          placeholder="Search RM Name..." 
                          value={bulkRmNameSearch}
                          onValueChange={setBulkRmNameSearch}
                          className="bg-gray-900 text-white h-9"
                        />
                        <CommandList className="max-h-48 overflow-y-auto">
                          <CommandEmpty>No materials found.</CommandEmpty>
                          <CommandGroup>
                            {materials.filter(m => 
                              (m.product_code && m.product_code.trim()) || (m.item_name && m.item_name.trim())
                            ).filter(m => 
                              !bulkRmNameSearch || 
                              (m.product_code || '').toLowerCase().includes(bulkRmNameSearch.toLowerCase()) ||
                              (m.item_name || '').toLowerCase().includes(bulkRmNameSearch.toLowerCase())
                            ).map((m) => {
                              const displayName = m.product_code || m.item_name;
                              return (
                                <CommandItem
                                  key={m.id}
                                  value={displayName}
                                  onSelect={() => {
                                    const updatedEntry = {
                                      ...bulkEntries[idx],
                                      rm_name: displayName,
                                      product: m.product_type || "",
                                      reel_size: m.reel_size_inches?.toString() || bulkEntries[idx].reel_size || ""
                                    };
                                    setBulkEntries(prev => prev.map((e, i) => i === idx ? updatedEntry : e));
                                    setBulkRmNameOpenIdx(null);
                                    setBulkRmNameSearch("");
                                  }}
                                  className="text-white hover:bg-purple-500/20 cursor-pointer"
                                >
                                  <Check className={cn("mr-2 h-4 w-4", entry.rm_name === displayName ? "opacity-100" : "opacity-0")} />
                                  <span>{displayName}</span>
                                  {m.product_type && <span className="text-cyan-400 ml-2 text-xs">({m.product_type})</span>}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Row 3: Weight, Price, Qty */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Reel Weight (kg) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={entry.reel_weight}
                      onChange={(e) => updateBulkEntry(idx, 'reel_weight', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      placeholder="e.g., 500"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Paper Price (₹/kg)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={entry.paper_price}
                      onChange={(e) => updateBulkEntry(idx, 'paper_price', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      placeholder="e.g., 32.50"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Quantity</Label>
                    <Input
                      type="number"
                      value={entry.qty || "1"}
                      onChange={(e) => updateBulkEntry(idx, 'qty', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      min="1"
                    />
                  </div>
                </div>
                
                {/* Row 4: Mill Reel No, Mill Name, Dealer Name */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Mill Reel No</Label>
                    <Input
                      value={entry.mill_reel_no || ""}
                      onChange={(e) => updateBulkEntry(idx, 'mill_reel_no', e.target.value)}
                      className="bg-black/50 border-gray-600/30 text-white h-9 mt-1"
                      placeholder="e.g., MR-12345"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Mill Name</Label>
                    <Select value={entry.mill_name || undefined} onValueChange={(v) => updateBulkEntry(idx, 'mill_name', v)}>
                      <SelectTrigger className="bg-black/50 border-gray-600/30 text-white h-9 mt-1">
                        <SelectValue placeholder="Select Mill" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-600/30 max-h-48">
                        {uniqueMillNames.filter(m => m && m.trim()).map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-300 text-xs font-medium">Dealer Name</Label>
                    <Select value={entry.dealer_name || undefined} onValueChange={(v) => updateBulkEntry(idx, 'dealer_name', v)}>
                      <SelectTrigger className="bg-black/50 border-gray-600/30 text-white h-9 mt-1">
                        <SelectValue placeholder="Select Dealer" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-600/30 max-h-48">
                        {uniqueDealerNames.filter(d => d && d.trim()).map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              onClick={addBulkRow}
              variant="outline"
              className="w-full border-gray-600/50 text-cyan-400 hover:bg-purple-500/20 h-10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Item
            </Button>
            
            {/* DC Creation Option for Bulk Add */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="create_dc_bulk"
                  checked={createDC}
                  onCheckedChange={(checked) => setCreateDC(checked)}
                  className="border-amber-500 data-[state=checked]:bg-amber-500"
                />
                <Label htmlFor="create_dc_bulk" className="text-amber-400 font-medium cursor-pointer">
                  📄 Create Delivery Challan (DC)
                </Label>
              </div>
              
              {createDC && (
                <div className="space-y-3 pt-3 border-t border-amber-500/30">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-amber-300 text-sm">Vendor/Dealer Name</Label>
                      <Select value={dcData.vendor_name} onValueChange={(v) => setDcData({ ...dcData, vendor_name: v })}>
                        <SelectTrigger className="bg-black/50 border-amber-500/30 text-white mt-1">
                          <SelectValue placeholder="Select vendor..." />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-amber-500/30 max-h-60">
                          {vendors.map(v => (
                            <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-amber-300 text-sm">Vehicle No.</Label>
                      <Input
                        value={dcData.vehicle_no}
                        onChange={(e) => setDcData({ ...dcData, vehicle_no: e.target.value })}
                        placeholder="e.g., TN01AB1234"
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-amber-300 text-sm">Driver Name</Label>
                      <Input
                        value={dcData.driver_name}
                        onChange={(e) => setDcData({ ...dcData, driver_name: e.target.value })}
                        placeholder="Driver name"
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-amber-300 text-sm">Remarks</Label>
                      <Input
                        value={dcData.remarks}
                        onChange={(e) => setDcData({ ...dcData, remarks: e.target.value })}
                        placeholder="Any remarks..."
                        className="bg-black/50 border-amber-500/30 text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-amber-300 text-sm">Manual Item Details (Optional)</Label>
                    <Textarea
                      value={dcData.manual_items}
                      onChange={(e) => setDcData({ ...dcData, manual_items: e.target.value })}
                      placeholder="Enter additional item details manually if needed..."
                      className="bg-black/50 border-amber-500/30 text-white mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t border-gray-600/20 pt-4">
            <Button variant="outline" onClick={() => setIsBulkAddDialogOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleBulkAdd} className="bg-purple-500 hover:bg-purple-600 text-white font-bold">
              <Plus className="h-4 w-4 mr-2" />
              Add {bulkEntries.length} Item{bulkEntries.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl">Edit Stock Item</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Common Fields */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-cyan-300">Reel ID *</Label>
                <Input
                  value={formData.reel_id}
                  onChange={(e) => setFormData({ ...formData, reel_id: e.target.value })}
                  className="bg-black/50 border-cyan-500/30 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-cyan-300">In Date</Label>
                <Input
                  type="date"
                  value={formData.in_date}
                  onChange={(e) => setFormData({ ...formData, in_date: e.target.value })}
                  className="bg-black/50 border-cyan-500/30 text-white mt-1 [color-scheme:dark]"
                />
              </div>
              <div>
                <Label className="text-cyan-300">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-black/50 border-cyan-500/30 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-cyan-500/30">
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="stock_used">Stock Used</SelectItem>
                    <SelectItem value="return">Return</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Product Type Selection */}
            <div>
              <Label className="text-cyan-300">Product Type</Label>
              <Select value={formData.product} onValueChange={(v) => setFormData({ ...formData, product: v })}>
                <SelectTrigger className="bg-black/50 border-cyan-500/30 text-white mt-1">
                  <SelectValue placeholder="Select product type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-cyan-500/30 max-h-60">
                  {allProductTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                      {PAPER_TYPES.includes(type) && <span className="ml-2 text-xs text-cyan-400">(Paper)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional Fields based on Product Type */}
            {isPaperProduct(formData.product) ? (
              /* Paper Product Edit Fields */
              <>
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <p className="text-cyan-300 text-sm font-medium">Paper Product Fields</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-cyan-300">RM Name</Label>
                    <Input
                      value={formData.rm_name}
                      onChange={(e) => setFormData({ ...formData, rm_name: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Reel Size (inches)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.reel_size}
                      onChange={(e) => setFormData({ ...formData, reel_size: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Reel Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.reel_weight}
                      onChange={(e) => setFormData({ ...formData, reel_weight: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-cyan-300">Paper Price (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.paper_price}
                      onChange={(e) => setFormData({ ...formData, paper_price: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Mill Reel No</Label>
                    <Input
                      value={formData.mill_reel_no}
                      onChange={(e) => setFormData({ ...formData, mill_reel_no: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Page No</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.page_no}
                      onChange={(e) => setFormData({ ...formData, page_no: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-cyan-300">Mill Name</Label>
                    <Input
                      value={formData.mill_name}
                      onChange={(e) => setFormData({ ...formData, mill_name: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-cyan-300">Dealer Name</Label>
                    <Input
                      value={formData.dealer_name}
                      onChange={(e) => setFormData({ ...formData, dealer_name: e.target.value })}
                      className="bg-black/50 border-cyan-500/30 text-white mt-1"
                    />
                  </div>
                </div>
              </>
            ) : formData.product ? (
              /* Non-Paper Product Edit Fields */
              <>
                <div className="p-3 bg-purple-500/10 border border-gray-600/30 rounded-lg">
                  <p className="text-purple-300 text-sm font-medium">Non-Paper Item Fields</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-purple-300">RM Name</Label>
                    <Input
                      value={formData.rm_name}
                      onChange={(e) => setFormData({ ...formData, rm_name: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300">Qty</Label>
                    <Input
                      type="number"
                      value={formData.qty}
                      onChange={(e) => setFormData({ ...formData, qty: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300">Units</Label>
                    <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                      <SelectTrigger className="bg-black/50 border-gray-600/30 text-white mt-1">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-600/30 max-h-60">
                        {unitOptions.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-purple-300">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-black/50 border-gray-600/30 text-white mt-1"
                    placeholder="Enter item description..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-purple-300">Tax Preference</Label>
                    <Select value={formData.tax_preference} onValueChange={(v) => setFormData({ ...formData, tax_preference: v })}>
                      <SelectTrigger className="bg-black/50 border-gray-600/30 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-600/30">
                        <SelectItem value="taxable">Taxable</SelectItem>
                        <SelectItem value="non_taxable">Non-Taxable</SelectItem>
                        <SelectItem value="exempt">Exempt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-300">CGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.cgst_rate}
                      onChange={(e) => setFormData({ ...formData, cgst_rate: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                      disabled={formData.tax_preference !== "taxable"}
                    />
                  </div>
                  <div>
                    <Label className="text-purple-300">SGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.sgst_rate}
                      onChange={(e) => setFormData({ ...formData, sgst_rate: e.target.value })}
                      className="bg-black/50 border-gray-600/30 text-white mt-1"
                      disabled={formData.tax_preference !== "taxable"}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-gray-400 border border-dashed border-gray-600 rounded-lg">
                <p>Please select a Product Type to see relevant fields</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleEditItem} className="bg-cyan-500 hover:bg-emerald-600 text-black font-bold">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Comment Dialog */}
      <AlertDialog open={statusChangeDialogOpen} onOpenChange={setStatusChangeDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-gray-600/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-cyan-400">
              {newStatus === "stock_used" ? "Mark as Stock Used" : "Mark as Return"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Please provide a comment for changing the status of <span className="font-bold text-white">{selectedItem?.reel_id}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            className="bg-black/50 border-gray-600/30 text-white"
            placeholder="Enter reason..."
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusChangeConfirm} className="bg-cyan-600 hover:bg-cyan-700 text-black">
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Stock Item</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete <span className="font-bold text-white">{selectedItem?.reel_id}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Item Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl flex items-center gap-2">
              <Eye className="h-5 w-5" />
              View Inventory Item
            </DialogTitle>
          </DialogHeader>
          {viewItem && (
            <div className="py-4 space-y-6">
              {/* Original Entry Section */}
              <div className="bg-black/40 rounded-lg p-4 border border-cyan-500/30">
                <h3 className="text-cyan-300 font-semibold mb-3 flex items-center gap-2">
                  <Boxes className="h-4 w-4" />
                  Current Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Reel ID:</span>
                    <span className="ml-2 text-white font-medium">{viewItem.reel_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Status:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      viewItem.status === 'available' ? 'bg-green-500/20 text-green-400' :
                      viewItem.status === 'stock_used' ? 'bg-red-500/20 text-red-400' :
                      'bg-cyan-600/20 text-cyan-400'
                    }`}>
                      {viewItem.status?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">RM Name:</span>
                    <span className="ml-2 text-white">{viewItem.rm_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Product:</span>
                    <span className="ml-2 text-white">{viewItem.product || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Reel Size:</span>
                    <span className="ml-2 text-white">{viewItem.reel_size ? `${viewItem.reel_size}"` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Current Weight:</span>
                    <span className="ml-2 text-white font-medium">{viewItem.reel_weight ? `${viewItem.reel_weight} Kg` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Original Weight:</span>
                    <span className="ml-2 text-white">{viewItem.original_reel_weight ? `${viewItem.original_reel_weight} Kg` : viewItem.original_weight ? `${viewItem.original_weight} Kg` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Paper Price:</span>
                    <span className="ml-2 text-white">{viewItem.paper_price ? `₹${viewItem.paper_price}/Kg` : '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Mill Reel No:</span>
                    <span className="ml-2 text-white">{viewItem.mill_reel_no || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Mill Name:</span>
                    <span className="ml-2 text-white">{viewItem.mill_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Dealer Name:</span>
                    <span className="ml-2 text-white">{viewItem.dealer_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">In Date:</span>
                    <span className="ml-2 text-white">{viewItem.in_date || '-'}</span>
                  </div>
                  {viewItem.is_bit_reel && (
                    <>
                      <div className="col-span-2">
                        <span className="text-cyan-400 font-medium">🔖 This is a Bit Reel</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Parent Reel:</span>
                        <span className="ml-2 text-cyan-300">{viewItem.parent_reel_id || '-'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Work Orders Used Section */}
              {(viewItem.used_in_work_order || (viewItem.used_in_work_orders && viewItem.used_in_work_orders.length > 0)) && (
                <div className="bg-black/40 rounded-lg p-4 border border-orange-500/30">
                  <h3 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Used In Work Orders
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {viewItem.used_in_work_order && (
                      <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">
                        {viewItem.used_in_work_order}
                      </span>
                    )}
                    {viewItem.used_in_work_orders?.filter(wo => wo !== viewItem.used_in_work_order).map((wo, idx) => (
                      <span key={idx} className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm">
                        {wo}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Comment Section */}
              {viewItem.used_comment && (
                <div className="bg-black/40 rounded-lg p-4 border border-gray-500/30">
                  <h3 className="text-gray-300 font-semibold mb-2 flex items-center gap-2">
                    💬 Comment
                  </h3>
                  <p className="text-gray-400 text-sm whitespace-pre-wrap">{viewItem.used_comment}</p>
                </div>
              )}

              {/* Modification History Section */}
              {viewItem.modification_history && viewItem.modification_history.length > 0 && (
                <div className="bg-black/40 rounded-lg p-4 border border-gray-600/30">
                  <h3 className="text-purple-300 font-semibold mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Modification History
                  </h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {viewItem.modification_history.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="bg-black/30 rounded p-3 border border-gray-600/20">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-cyan-400 text-xs font-medium">{entry.action?.toUpperCase()}</span>
                          <span className="text-gray-500 text-xs">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}
                          </span>
                        </div>
                        {entry.changes && Object.keys(entry.changes).length > 0 && (
                          <div className="space-y-1 text-xs">
                            {Object.entries(entry.changes).map(([field, change]) => (
                              <div key={field} className="flex gap-2">
                                <span className="text-gray-400 capitalize">{field.replace(/_/g, ' ')}:</span>
                                <span className="text-red-400 line-through">{change.old || '-'}</span>
                                <span className="text-gray-500">→</span>
                                <span className="text-green-400">{change.new || '-'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="flex justify-between text-xs text-gray-500 border-t border-gray-700 pt-3">
                <span>Created: {viewItem.created_at ? new Date(viewItem.created_at).toLocaleString() : '-'}</span>
                <span>Updated: {viewItem.updated_at ? new Date(viewItem.updated_at).toLocaleString() : '-'}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)} className="border-gray-500 text-gray-300">
              Close
            </Button>
            <Button onClick={() => { setIsViewDialogOpen(false); openEditDialog(viewItem); }} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Labels Dialog */}
      <Dialog open={isPrintDialogOpen} onOpenChange={setIsPrintDialogOpen}>
        <DialogContent className="bg-gray-900 border-pink-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-pink-400 text-xl flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Print RM Labels
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-300">
              You are about to print <span className="font-bold text-pink-400">{
                (() => {
                  const selectedItems = inventory.filter(i => selectedForPrint.includes(i.id));
                  const nonCorrugated = selectedItems.filter(i => i.product !== 'Corrugated Roll').length;
                  const corrugatedTotal = selectedItems
                    .filter(i => i.product === 'Corrugated Roll')
                    .reduce((sum, i) => sum + (corrugatedLabelCounts[i.id] || 1), 0);
                  return nonCorrugated + corrugatedTotal;
                })()
              }</span> label(s).
            </p>
            
            {/* Corrugated Roll Label Quantity Section */}
            {(() => {
              const corrugatedItems = inventory.filter(i => selectedForPrint.includes(i.id) && i.product === 'Corrugated Roll');
              if (corrugatedItems.length === 0) return null;
              return (
                <div className="p-4 bg-black/50 rounded-lg border border-emerald-500/30" data-testid="corrugated-label-count-section">
                  <h4 className="text-sm font-semibold text-emerald-300 mb-3 flex items-center gap-2">
                    <Printer className="h-4 w-4" />
                    Corrugated Roll — How many labels?
                  </h4>
                  <p className="text-xs text-gray-400 mb-3">Default is set to the available quantity of each roll. Adjust as needed.</p>
                  <div className="space-y-2">
                    {corrugatedItems.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-black/30 rounded-lg border border-gray-700">
                        <div className="flex-1">
                          <span className="text-white font-mono font-bold text-sm">{item.reel_id}</span>
                          <span className="text-gray-400 text-xs ml-2">({item.rm_name || 'N/A'})</span>
                          <span className="text-emerald-400 text-xs ml-2">Qty: {item.qty || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-gray-400 text-xs whitespace-nowrap">Labels to print:</Label>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            data-testid={`corrugated-label-count-${item.reel_id}`}
                            value={corrugatedLabelCounts[item.id] || 1}
                            onChange={(e) => setCorrugatedLabelCounts(prev => ({
                              ...prev,
                              [item.id]: Math.max(1, parseInt(e.target.value) || 1)
                            }))}
                            className="w-20 bg-black/50 border-emerald-500/30 text-white text-center"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Label Size Selection - Simplified */}
            <div className="p-4 bg-black/50 rounded-lg border border-pink-500/30">
              <h4 className="text-sm font-semibold text-pink-300 mb-3">Select Label Size</h4>
              
              <div className="space-y-3">
                {/* Standard Label Option with Edit Button */}
                <div 
                  className={`p-3 rounded-lg transition-all ${
                    !useCustomSize 
                      ? 'bg-pink-500/20 border-2 border-pink-500' 
                      : 'bg-black/30 border-2 border-transparent hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-3 cursor-pointer flex-1">
                      <input
                        type="radio"
                        name="labelSize"
                        checked={!useCustomSize}
                        onChange={() => {
                          setUseCustomSize(false);
                          setEditingStandardSize(false);
                        }}
                        className="accent-pink-500"
                      />
                      <span className="text-white font-medium">
                        {standardLabelWidth}" x {standardLabelHeight}" (Standard Label)
                      </span>
                    </label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        setEditingStandardSize(!editingStandardSize);
                        setUseCustomSize(false);
                      }}
                      className="text-pink-400 hover:text-pink-300 hover:bg-pink-500/20 h-8 px-2"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Editable Standard Size Inputs */}
                  {editingStandardSize && !useCustomSize && (
                    <div className="mt-3 p-3 bg-black/30 rounded-lg border border-pink-500/30">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-pink-300 text-xs">Width (inches)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            min="1"
                            max="10"
                            value={standardLabelWidth}
                            onChange={(e) => setStandardLabelWidth(e.target.value)}
                            className="bg-black/50 border-pink-500/30 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-pink-300 text-xs">Height (inches)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            min="1"
                            max="10"
                            value={standardLabelHeight}
                            onChange={(e) => setStandardLabelHeight(e.target.value)}
                            className="bg-black/50 border-pink-500/30 text-white mt-1"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-green-400 mt-2">
                        Standard size: {standardLabelWidth}" x {standardLabelHeight}"
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Custom Size Option */}
                <label 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    useCustomSize 
                      ? 'bg-pink-500/20 border-2 border-pink-500' 
                      : 'bg-black/30 border-2 border-transparent hover:bg-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="labelSize"
                    checked={useCustomSize}
                    onChange={() => {
                      setUseCustomSize(true);
                      setEditingStandardSize(false);
                    }}
                    className="accent-pink-500"
                  />
                  <span className="text-white font-medium">Manual Entry (Custom Size)</span>
                </label>
                
                {/* Custom Size Inputs */}
                {useCustomSize && (
                  <div className="p-3 bg-black/30 rounded-lg border border-gray-700 ml-6">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-pink-300 text-xs">Width (inches)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="1"
                          max="10"
                          placeholder="e.g., 3"
                          value={customLabelWidth}
                          onChange={(e) => setCustomLabelWidth(e.target.value)}
                          className="bg-black/50 border-pink-500/30 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-pink-300 text-xs">Height (inches)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="1"
                          max="10"
                          placeholder="e.g., 5"
                          value={customLabelHeight}
                          onChange={(e) => setCustomLabelHeight(e.target.value)}
                          className="bg-black/50 border-pink-500/30 text-white mt-1"
                        />
                      </div>
                    </div>
                    {customLabelWidth && customLabelHeight && (
                      <p className="text-xs text-green-400 mt-2">
                        Custom size: {customLabelWidth}" x {customLabelHeight}"
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Label Preview - Updated with logo at top, QR at bottom */}
            <div className="bg-black/50 rounded-lg p-4 border border-pink-500/30">
              <h4 className="text-sm font-semibold text-pink-300 mb-2">Label Preview:</h4>
              <div className="border-2 border-gray-600 rounded bg-white text-black text-sm max-w-xs overflow-hidden">
                {/* Header with Logo */}
                <div className="flex items-center bg-teal-700 text-white p-2">
                  <div className="w-8 h-8 bg-white flex items-center justify-center text-[6px] rounded mr-2">LOGO</div>
                  <div className="text-center flex-1">
                    <div className="font-bold text-[10px]">RM LABEL</div>
                    <div className="text-[7px] opacity-80">Challenge Pack Industries</div>
                  </div>
                </div>
                {/* Stock ID Box */}
                <div className="bg-teal-700 text-white text-center py-1 mx-2 mt-2 rounded">
                  <div className="text-[6px] opacity-70">STOCK ID</div>
                  <div className="font-bold text-[10px] font-mono">[Auto]</div>
                </div>
                {/* Content Preview */}
                <div className="p-2 space-y-0.5 text-[8px]">
                  <div className="flex justify-between border-b border-dotted border-gray-400 pb-0.5"><span className="font-semibold">Reel Size:</span><span>[Size]"</span></div>
                  <div className="flex justify-between bg-gray-100 border-b border-dotted border-gray-400 pb-0.5 px-0.5"><span className="font-semibold">RM Name:</span><span>[RM Name]</span></div>
                  <div className="flex justify-between bg-amber-200 border-b border-dotted border-gray-400 pb-0.5 px-0.5"><span className="font-semibold">Weight:</span><span className="font-bold">[Weight] kg</span></div>
                  <div className="flex justify-between bg-gray-100 pb-0.5 px-0.5"><span className="font-semibold">Product:</span><span>[Product]</span></div>
                </div>
                {/* QR at Bottom */}
                <div className="border-t-2 border-teal-700 p-2 text-center">
                  <div className="text-[6px] text-gray-500 mb-1">Scan QR Code to view label</div>
                  <div className="inline-block w-10 h-10 bg-gray-800 relative">
                    <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white text-black text-[5px] px-0.5 font-bold">CPI</span>
                  </div>
                </div>
                {/* Footer */}
                <div className="text-center text-[6px] text-gray-500 pb-1">
                  Printed: {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="text-sm text-gray-400">
              <p>• Labels will be saved to Google Drive</p>
              <p>• QR code links to the saved label image</p>
              <p>• Labels will open in print window after upload</p>
            </div>
            
            {/* Upload Progress */}
            {uploadingLabels && (
              <div className="p-3 bg-black/50 rounded-lg border border-pink-500/30">
                <div className="flex items-center gap-2 text-pink-300">
                  <div className="animate-spin h-4 w-4 border-2 border-pink-500 border-t-transparent rounded-full"></div>
                  <span>Uploading labels to Google Drive...</span>
                </div>
                <div className="mt-2 bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-pink-500 h-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {uploadProgress.current} of {uploadProgress.total} labels
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPrintDialogOpen(false)} className="border-gray-500 text-gray-300" disabled={uploadingLabels}>
              Cancel
            </Button>
            <Button 
              onClick={handlePrintLabels} 
              disabled={(useCustomSize && (!customLabelWidth || !customLabelHeight)) || uploadingLabels}
              className="bg-pink-500 hover:bg-pink-600 text-white font-bold disabled:opacity-50"
            >
              {uploadingLabels ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Printer className="h-4 w-4 mr-2" />
                  Upload & Print {(() => {
                    const selectedItems = inventory.filter(i => selectedForPrint.includes(i.id));
                    const nonCorrugated = selectedItems.filter(i => i.product !== 'Corrugated Roll').length;
                    const corrugatedTotal = selectedItems
                      .filter(i => i.product === 'Corrugated Roll')
                      .reduce((sum, i) => sum + (corrugatedLabelCounts[i.id] || 1), 0);
                    return nonCorrugated + corrugatedTotal;
                  })()} Labels
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bit Reel Dialog - For Partial Usage */}
      <Dialog open={bitReelDialogOpen} onOpenChange={setBitReelDialogOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl flex items-center gap-2">
              ✂️ Create Bit Reel (Partial Usage)
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Original Item Info */}
            <div className="p-3 bg-black/50 rounded-lg border border-cyan-500/30">
              <p className="text-sm text-gray-400 mb-1">Original Item</p>
              <p className="text-lg font-bold text-white">{selectedItem?.reel_id}</p>
              <p className="text-sm text-cyan-400">
                Original Weight: <span className="font-bold">{selectedItem?.reel_weight || 0} Kg</span>
              </p>
              {selectedItem?.rm_name && (
                <p className="text-xs text-gray-400 mt-1">{selectedItem.rm_name}</p>
              )}
            </div>

            {/* Used Weight */}
            <div>
              <Label className="text-cyan-300">Used Weight (Kg) *</Label>
              <Input
                type="number"
                step="0.01"
                value={bitReelData.usedWeight}
                onChange={(e) => {
                  const usedVal = e.target.value;
                  setBitReelData(prev => ({
                    ...prev,
                    usedWeight: usedVal,
                    // Auto-calculate balance
                    balanceWeight: usedVal ? 
                      Math.max(0, (selectedItem?.reel_weight || 0) - parseFloat(usedVal)).toFixed(2) : ""
                  }));
                }}
                className="bg-black/50 border-cyan-500/30 text-white mt-1"
                placeholder="Enter weight used..."
              />
            </div>

            {/* Balance Weight (Bit Reel) */}
            <div>
              <Label className="text-cyan-300">Balance Weight - Bit Reel (Kg) *</Label>
              <Input
                type="number"
                step="0.01"
                value={bitReelData.balanceWeight}
                onChange={(e) => setBitReelData(prev => ({ ...prev, balanceWeight: e.target.value }))}
                className="bg-black/50 border-cyan-500/30 text-white mt-1"
                placeholder="Remaining weight..."
              />
              <p className="text-xs text-gray-500 mt-1">
                This will create a new item: {selectedItem?.reel_id}-BIT
              </p>
            </div>

            {/* Work Order Number (Optional) */}
            <div>
              <Label className="text-cyan-300">Work Order Number (Optional)</Label>
              <Popover open={woSearchBitReelOpen} onOpenChange={setWoSearchBitReelOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={woSearchBitReelOpen}
                    className="w-full justify-between bg-black/50 border-cyan-500/30 text-white mt-1 hover:bg-black/70"
                  >
                    {bitReelData.workOrderNumber || "Search Work Order..."}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0 bg-gray-900 border-cyan-500/30">
                  <Command className="bg-gray-900" shouldFilter={false}>
                    <CommandInput 
                      placeholder="Search WO number or customer..." 
                      value={woSearchBitReel}
                      onValueChange={setWoSearchBitReel}
                      className="bg-gray-900 text-white"
                    />
                    <CommandList className="max-h-60 overflow-y-auto">
                      <CommandEmpty>No work orders found.</CommandEmpty>
                      <CommandGroup>
                        {filteredWorkOrdersBitReel.map((wo) => {
                          const woNum = wo.header?.work_order_number || wo.wo_number || '';
                          const custName = wo.header?.customer_name || wo.customer_name || '';
                          return (
                            <CommandItem
                              key={wo.id}
                              value={woNum}
                              onSelect={() => {
                                setBitReelData(prev => ({ ...prev, workOrderNumber: woNum }));
                                setWoSearchBitReelOpen(false);
                              }}
                              className="text-white hover:bg-cyan-500/20 cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  bitReelData.workOrderNumber === woNum ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div>
                                <span className="font-bold">{woNum}</span>
                                <span className="text-gray-400 ml-2">- {custName}</span>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Comment */}
            <div>
              <Label className="text-cyan-300">Comment (Optional)</Label>
              <Textarea
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                className="bg-black/50 border-cyan-500/30 text-white mt-1"
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Summary */}
            {bitReelData.usedWeight && bitReelData.balanceWeight && (
              <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                <p className="text-sm text-cyan-300 font-semibold mb-1">Summary:</p>
                <p className="text-xs text-gray-300">
                  • <span className="text-white font-bold">{selectedItem?.reel_id}</span> → Stock Used ({bitReelData.usedWeight} Kg)
                </p>
                <p className="text-xs text-gray-300">
                  • New Bit Reel: <span className="text-cyan-400 font-bold">{selectedItem?.reel_id}-BIT</span> ({bitReelData.balanceWeight} Kg)
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setBitReelDialogOpen(false);
                setSelectedItem(null);
                setStatusComment("");
                setBitReelData({ usedWeight: "", balanceWeight: "", workOrderNumber: "" });
              }} 
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBitReelConfirm} 
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
            >
              Create Bit Reel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WO Leftover Material Dialog */}
      <Dialog open={leftoverDialogOpen} onOpenChange={setLeftoverDialogOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Record Work Order Leftover
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Work Order Selection */}
            <div>
              <Label className="text-amber-300">Work Order Number *</Label>
              <Popover open={woSearchLeftoverOpen} onOpenChange={setWoSearchLeftoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={woSearchLeftoverOpen}
                    className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1 hover:bg-black/70"
                  >
                    {leftoverData.workOrderNumber || "Search Work Order..."}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0 bg-gray-900 border-amber-500/30">
                  <Command className="bg-gray-900" shouldFilter={false}>
                    <CommandInput 
                      placeholder="Search WO number or customer..." 
                      value={woSearchLeftover}
                      onValueChange={setWoSearchLeftover}
                      className="bg-gray-900 text-white"
                    />
                    <CommandList className="max-h-60 overflow-y-auto">
                      <CommandEmpty>No work orders found.</CommandEmpty>
                      <CommandGroup>
                        {filteredWorkOrdersLeftover.map((wo) => {
                          const woNum = wo.header?.work_order_number || wo.wo_number || '';
                          const custName = wo.header?.customer_name || wo.customer_name || '';
                          return (
                            <CommandItem
                              key={wo.id}
                              value={woNum}
                              onSelect={() => {
                                setLeftoverData(prev => ({
                                  ...prev,
                                  workOrderNumber: woNum,
                                  // Reset parent reel selection when WO changes (filter scope changes)
                                  parentReelId: "",
                                  parentReelWeight: 0,
                                  usedWeight: "",
                                  leftoverWeight: "",
                                  reelSize: "",
                                  rmName: ""
                                }));
                                setParentReelSearch("");
                                setWoSearchLeftoverOpen(false);
                              }}
                              className="text-white hover:bg-amber-500/20 cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  leftoverData.workOrderNumber === woNum ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div>
                                <span className="font-bold">{woNum}</span>
                                <span className="text-gray-400 ml-2">- {custName}</span>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Material Type */}
            <div>
              <Label className="text-amber-300">Material Type *</Label>
              <Select value={leftoverData.materialType} onValueChange={(v) => setLeftoverData(prev => ({ ...prev, materialType: v }))}>
                <SelectTrigger className="bg-black/50 border-amber-500/30 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-amber-500/30">
                  <SelectItem value="bit_reel">✂️ Bit Reel (Paper)</SelectItem>
                  <SelectItem value="corrugated_roll">📦 Corrugated Roll</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bit Reel Fields */}
            {leftoverData.materialType === "bit_reel" && (
              <>
                {/* Parent Reel ID - Searchable Dropdown */}
                <div>
                  <Label className="text-amber-300">Parent Reel ID (Select from Available Stock) *</Label>
                  <Popover open={parentReelOpen} onOpenChange={setParentReelOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={parentReelOpen}
                        className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1 hover:bg-black/70"
                      >
                        {leftoverData.parentReelId || "Search Parent Reel..."}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0 bg-gray-900 border-amber-500/30">
                      <Command className="bg-gray-900" shouldFilter={false}>
                        <CommandInput 
                          placeholder="Search by Reel ID or RM Name..." 
                          value={parentReelSearch}
                          onValueChange={setParentReelSearch}
                          className="bg-gray-900 text-white"
                        />
                        <CommandList className="max-h-60 overflow-y-auto">
                          <CommandEmpty>No available reels found.</CommandEmpty>
                          <CommandGroup>
                            {filteredAvailableReels.map((inv) => (
                              <CommandItem
                                key={inv.id}
                                value={inv.reel_id}
                                onSelect={() => {
                                  setLeftoverData(prev => ({
                                    ...prev,
                                    parentReelId: inv.reel_id,
                                    parentReelWeight: inv.reel_weight || 0,
                                    reelSize: inv.reel_size?.toString() || "",
                                    rmName: inv.rm_name || "",
                                    usedWeight: "",
                                    leftoverWeight: ""
                                  }));
                                  setParentReelOpen(false);
                                }}
                                className="text-white hover:bg-amber-500/20 cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    leftoverData.parentReelId === inv.reel_id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-bold">{inv.reel_id}</span>
                                  <span className="text-xs text-gray-400">
                                    {inv.rm_name || "N/A"} | Size: {inv.reel_size || "N/A"}" | {inv.reel_weight || 0} Kg
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Parent Reel Info */}
                {leftoverData.parentReelId && (
                  <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <p className="text-sm text-amber-300 font-semibold">Parent Reel Info:</p>
                    <p className="text-sm text-white">
                      <span className="font-bold">{leftoverData.parentReelId}</span> - Original Weight: <span className="font-bold">{leftoverData.parentReelWeight} Kg</span>
                    </p>
                    {leftoverData.rmName && (
                      <p className="text-xs text-gray-400 mt-1">{leftoverData.rmName}</p>
                    )}
                  </div>
                )}

                {/* Leftover Weight - Primary input (what Admin sees physically) */}
                <div>
                  <Label className="text-amber-300">Leftover Weight (Kg) - Bit Reel remaining *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={leftoverData.leftoverWeight}
                    onChange={(e) => {
                      const leftoverVal = e.target.value;
                      const used = leftoverVal
                        ? Math.max(0, leftoverData.parentReelWeight - parseFloat(leftoverVal)).toFixed(2)
                        : "";
                      setLeftoverData(prev => ({
                        ...prev,
                        leftoverWeight: leftoverVal,
                        usedWeight: used
                      }));
                    }}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    placeholder="Enter physical leftover weight..."
                    disabled={!leftoverData.parentReelId}
                    data-testid="leftover-weight-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This creates a new bit reel: {leftoverData.parentReelId}-BIT
                  </p>
                </div>

                {/* Used Weight - Auto-calculated (read-only display) */}
                <div>
                  <Label className="text-amber-300">Used Weight (Kg) - Auto-calculated (Parent − Leftover)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={leftoverData.usedWeight}
                    readOnly
                    className="bg-black/30 border-amber-500/20 text-gray-300 mt-1 cursor-not-allowed"
                    placeholder="Auto-calculated from parent − leftover"
                    data-testid="used-weight-derived"
                  />
                </div>

                {/* Size - Auto-populated */}
                <div>
                  <Label className="text-amber-300">Size (inches) - Auto-populated</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={leftoverData.reelSize}
                    onChange={(e) => setLeftoverData(prev => ({ ...prev, reelSize: e.target.value }))}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    placeholder="Auto-filled from parent reel"
                  />
                </div>

                {/* RM Name - Auto-populated */}
                <div>
                  <Label className="text-amber-300">RM Name - Auto-populated</Label>
                  <Input
                    value={leftoverData.rmName}
                    onChange={(e) => setLeftoverData(prev => ({ ...prev, rmName: e.target.value }))}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    placeholder="Auto-filled from parent reel"
                  />
                </div>

                {/* Summary */}
                {leftoverData.usedWeight && leftoverData.leftoverWeight && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-sm text-green-300 font-semibold mb-1">Summary:</p>
                    <p className="text-xs text-gray-300">
                      • <span className="text-white font-bold">{leftoverData.parentReelId}</span>: {leftoverData.parentReelWeight} Kg → <span className="text-amber-400 font-bold">{leftoverData.usedWeight} Kg</span> (Used in WO)
                    </p>
                    <p className="text-xs text-gray-300">
                      • New Bit Reel: <span className="text-cyan-400 font-bold">{leftoverData.parentReelId}-BIT</span> → <span className="text-green-400 font-bold">{leftoverData.leftoverWeight} Kg</span> (Leftover)
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Corrugated Roll Fields */}
            {leftoverData.materialType === "corrugated_roll" && (
              <>
                {/* Parent Reel ID - Searchable Dropdown */}
                <div>
                  <Label className="text-amber-300">Parent Reel ID (Select from Available Stock)</Label>
                  <Popover open={parentReelOpen} onOpenChange={setParentReelOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={parentReelOpen}
                        className="w-full justify-between bg-black/50 border-amber-500/30 text-white mt-1 hover:bg-black/70"
                      >
                        {leftoverData.parentReelId || "Search Parent Reel..."}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0 bg-gray-900 border-amber-500/30">
                      <Command className="bg-gray-900" shouldFilter={false}>
                        <CommandInput 
                          placeholder="Search by Reel ID or RM Name..." 
                          value={parentReelSearch}
                          onValueChange={setParentReelSearch}
                          className="bg-gray-900 text-white"
                        />
                        <CommandList className="max-h-60 overflow-y-auto">
                          <CommandEmpty>No available reels found.</CommandEmpty>
                          <CommandGroup>
                            {filteredAvailableReels.map((inv) => (
                              <CommandItem
                                key={inv.id}
                                value={inv.reel_id}
                                onSelect={() => {
                                  setLeftoverData(prev => ({
                                    ...prev,
                                    parentReelId: inv.reel_id,
                                    reelSize: inv.reel_size?.toString() || "",
                                    rmName: inv.rm_name || ""
                                  }));
                                  setParentReelOpen(false);
                                }}
                                className="text-white hover:bg-amber-500/20 cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    leftoverData.parentReelId === inv.reel_id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-bold">{inv.reel_id}</span>
                                  <span className="text-xs text-gray-400">
                                    {inv.rm_name || "N/A"} | Size: {inv.reel_size || "N/A"}" | {inv.reel_weight || 0} Kg
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Size - Auto-populated */}
                <div>
                  <Label className="text-amber-300">Size (inches) - Auto-populated</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={leftoverData.reelSize}
                    onChange={(e) => setLeftoverData(prev => ({ ...prev, reelSize: e.target.value }))}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    placeholder="Auto-filled from parent reel"
                  />
                </div>

                {/* RM Name - Auto-populated */}
                <div>
                  <Label className="text-amber-300">RM Name - Auto-populated</Label>
                  <Input
                    value={leftoverData.rmName}
                    onChange={(e) => setLeftoverData(prev => ({ ...prev, rmName: e.target.value }))}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    placeholder="Auto-filled from parent reel"
                  />
                </div>

                <div>
                  <Label className="text-amber-300">Quantity (Nos) *</Label>
                  <Input
                    type="number"
                    value={leftoverData.quantity}
                    onChange={(e) => setLeftoverData(prev => ({ ...prev, quantity: e.target.value }))}
                    className="bg-black/50 border-amber-500/30 text-white mt-1"
                    placeholder="Enter quantity"
                  />
                </div>
              </>
            )}

            <div>
              <Label className="text-amber-300">Comment (Optional)</Label>
              <Textarea
                value={leftoverData.comment}
                onChange={(e) => setLeftoverData(prev => ({ ...prev, comment: e.target.value }))}
                className="bg-black/50 border-amber-500/30 text-white mt-1"
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Info Box */}
            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
              <p className="text-xs text-gray-400">
                💡 This will create a new inventory item:
              </p>
              <p className="text-sm text-amber-300 font-medium mt-1">
                {leftoverData.materialType === "bit_reel" 
                  ? `BIT-YYYYMMDD-XXX (${leftoverData.weight || "?"} Kg)`
                  : `CR-YYYYMMDD-XXX (${leftoverData.quantity || "?"} Nos)`
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setLeftoverDialogOpen(false)} 
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLeftoverSubmit} 
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
            >
              Add Leftover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Date Range Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-600/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl flex items-center gap-2">
              <Download className="h-5 w-5" />
              Export Inventory to CSV
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-300 text-sm">
              Select a date range to filter the exported data. Leave empty to export all data.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">From Date</Label>
                <Input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                  className="bg-black/50 border-gray-600/30 text-white mt-1 [color-scheme:dark]"
                />
              </div>
              <div>
                <Label className="text-gray-300">To Date</Label>
                <Input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                  className="bg-black/50 border-gray-600/30 text-white mt-1 [color-scheme:dark]"
                />
              </div>
            </div>
            
            {(exportFromDate || exportToDate) && (
              <p className="text-xs text-gray-500">
                Will export inventory items with in_date {exportFromDate ? `from ${exportFromDate}` : ""} {exportToDate ? `to ${exportToDate}` : ""}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setIsExportDialogOpen(false); setExportFromDate(""); setExportToDate(""); }}
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleExportCSV}
              className="bg-cyan-600 hover:bg-cyan-700 text-black font-bold"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog (Password Protected) */}
      <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-xl flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Archive Old Inventory Data
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-300 text-sm flex items-center gap-2">
                <Lock className="h-4 w-4" />
                This action will archive (move to backup) old inventory items that are not "available" status.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-300">Archive items older than (days)</Label>
              <Input
                type="number"
                value={archiveDays}
                onChange={(e) => setArchiveDays(parseInt(e.target.value) || 365)}
                className="bg-black/50 border-red-500/30 text-white mt-1"
                min={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                Items with in_date before {new Date(Date.now() - archiveDays * 24 * 60 * 60 * 1000).toLocaleDateString()} will be archived
              </p>
            </div>
            
            <div>
              <Label className="text-gray-300">Enter Password to Confirm</Label>
              <PasswordInput
                value={archivePassword}
                onChange={(e) => setArchivePassword(e.target.value)}
                className="bg-black/50 border-red-500/30 text-white mt-1"
                placeholder="Enter archival password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setIsArchiveDialogOpen(false); setArchivePassword(""); }}
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleArchive}
              disabled={isArchiving || !archivePassword}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isArchiving ? "Archiving..." : "Archive Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore from Stock Used Dialog (Password Protected) */}
      <Dialog open={isRestoreDialogOpen} onOpenChange={(open) => {
        setIsRestoreDialogOpen(open);
        if (!open) {
          setRestorePassword("");
          setItemToRestore(null);
        }
      }}>
        <DialogContent className="bg-gray-900 border-green-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-400 text-xl flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Restore to Available Stock
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-300 text-sm">
                You are about to restore this item from "Stock Used" back to "Available" status.
              </p>
              {itemToRestore && (
                <div className="mt-2 text-sm">
                  <p className="text-white font-bold">{itemToRestore.reel_id}</p>
                  <p className="text-gray-400">{itemToRestore.rm_name || "N/A"} - {itemToRestore.reel_weight || 0} Kg</p>
                  {itemToRestore.used_in_work_order && (
                    <p className="text-amber-400 text-xs mt-1">Used in WO: {itemToRestore.used_in_work_order}</p>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Enter Password to Confirm
              </Label>
              <PasswordInput
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                className="bg-black/50 border-green-500/30 text-white mt-1"
                placeholder="Enter restore password"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && restorePassword) {
                    handleRestoreConfirm();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setIsRestoreDialogOpen(false); setRestorePassword(""); setItemToRestore(null); }}
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreConfirm}
              disabled={!restorePassword}
              className="bg-green-500 hover:bg-green-600 text-black font-bold"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Inventory Dialog */}
      <Dialog open={isDeleteAllDialogOpen} onOpenChange={(open) => {
        setIsDeleteAllDialogOpen(open);
        if (!open) {
          setDeleteAllPassword("");
          setDeleteAllSelection({ availableStock: false, stockUsed: false, sentForPrinting: false, sentForCorrugation: false });
        }
      }}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-xl flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete All Inventory Data
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-red-300 text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                WARNING: This action cannot be undone!
              </p>
              <p className="text-red-200 text-xs mt-1">
                All selected inventory items will be permanently deleted.
              </p>
            </div>
            
            <div className="space-y-3">
              <Label className="text-gray-300 font-semibold">Select Stock Type to Delete:</Label>
              
              <div className="flex items-center space-x-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="deleteAvailable"
                  checked={deleteAllSelection.availableStock}
                  onChange={(e) => setDeleteAllSelection(prev => ({ ...prev, availableStock: e.target.checked }))}
                  className="w-5 h-5 rounded border-emerald-500 text-emerald-500 focus:ring-emerald-500"
                />
                <label htmlFor="deleteAvailable" className="text-emerald-300 font-medium cursor-pointer">
                  Available Stock ({inventory.filter(i => i.status === 'available').length} items)
                </label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="deleteStockUsed"
                  checked={deleteAllSelection.stockUsed}
                  onChange={(e) => setDeleteAllSelection(prev => ({ ...prev, stockUsed: e.target.checked }))}
                  className="w-5 h-5 rounded border-amber-500 text-amber-500 focus:ring-amber-500"
                />
                <label htmlFor="deleteStockUsed" className="text-amber-300 font-medium cursor-pointer">
                  Stock Used ({inventory.filter(i => i.status === 'stock_used').length} items)
                </label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="deleteSentForPrinting"
                  checked={deleteAllSelection.sentForPrinting}
                  onChange={(e) => setDeleteAllSelection(prev => ({ ...prev, sentForPrinting: e.target.checked }))}
                  className="w-5 h-5 rounded border-purple-500 text-purple-500 focus:ring-purple-500"
                />
                <label htmlFor="deleteSentForPrinting" className="text-purple-300 font-medium cursor-pointer">
                  Sent for Printing ({inventory.filter(i => i.status === 'printed' || i.status === 'sent_for_printing').length} items)
                </label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="deleteSentForCorrugation"
                  checked={deleteAllSelection.sentForCorrugation}
                  onChange={(e) => setDeleteAllSelection(prev => ({ ...prev, sentForCorrugation: e.target.checked }))}
                  className="w-5 h-5 rounded border-orange-500 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="deleteSentForCorrugation" className="text-orange-300 font-medium cursor-pointer">
                  Sent for Corrugation ({inventory.filter(i => i.status === 'sent_for_corrugation' || i.status === 'received_corrugation').length} items)
                </label>
              </div>
            </div>
            
            <div>
              <Label className="text-gray-300">Enter Password to Confirm</Label>
              <PasswordInput
                value={deleteAllPassword}
                onChange={(e) => setDeleteAllPassword(e.target.value)}
                className="bg-black/50 border-red-500/30 text-white mt-1"
                placeholder="Enter delete password"
              />
              <p className="text-xs text-gray-500 mt-1">Password: Cpidelete#909</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { 
                setIsDeleteAllDialogOpen(false); 
                setDeleteAllPassword(""); 
                setDeleteAllSelection({ availableStock: false, stockUsed: false, sentForPrinting: false, sentForCorrugation: false }); 
              }}
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteAll}
              disabled={isDeleting || !deleteAllPassword || (!deleteAllSelection.availableStock && !deleteAllSelection.stockUsed)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              {isDeleting ? "Deleting..." : "Delete Selected"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

export default StoreInventory;
